import { MaintenanceHelp } from "./MaintenanceHelp";
import type { SetupInputConnection } from "@/api/setup-input";
import { storageKindLabel } from "@/lib/storage-kind-label";
import { AppLimitReachedNotice } from "@/features/billing/AppLimitReachedNotice";
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Check,
  CheckCircle2,
  Clipboard,
  Download,
  ExternalLink,
  Loader2,
  Paperclip,
  RefreshCcw,
  Trash2,
  Wrench,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { ApiError, apiErrorMessage, consoleBffRequestUrl } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useStorageList } from "@/features/storages/hooks/useStorages";
import { isStorageIntegratedAvailable } from "@/features/storages/storage-ux";
import { useAuth } from "@/hooks/useAuth";
import {
  fetchCurrentKintoneEnvironment,
  registerInitialKintoneEnvironment,
  verifyKintoneEnvironmentOwnership,
  type InitialEnvironmentRegistrationRequest,
  type KintoneConnectionAccess,
} from "@/api/kintone-environment";
import { AppBindingModeSelector } from "./AppBindingModeSelector";
import { EasySetupFlow } from "./EasySetupFlow";
import { AppBaseFolderPickerButton } from "./AppBaseFolderPickerButton";
import { useInitialKintoneSetupSession } from "./InitialKintoneSetupSessionContext";
import { HealthWarningBanner } from "./HealthWarningBanner";
import { MaintenanceDialog, type MaintenanceOperationStatus } from "./MaintenanceDialog";
import { AttachmentEnvironmentRestoreSection } from "./AttachmentEnvironmentRestoreSection";
import { AppBindingSettingsSaveGate, AutomaticAttachmentAlignmentPanel } from "./AutomaticAttachmentAlignmentPanel";
import { useAutomaticAttachmentAlignment } from "./hooks/useAutomaticAttachmentAlignment";
import { AttachmentStorageAlignmentSection } from "./AttachmentStorageAlignmentSection";
import { OrphanRecordFolderCleanupSection } from "./OrphanRecordFolderCleanupSection";
import type { AppBindingResponse } from "@/types/api";
import {
  useAppBindingDetail,
  useDeleteAppBinding,
  useDisconnectPluginLinkage,
  useEnsurePluginInstallation,
  useFeatures,
  useHealthCheck,
  useImportFieldSettings,
  useKintoneAdminCredentials,
  useProbeKintoneAppFields,
  useRequestReflection,
  useSecureAccessSettings,
  useUpsertAppBinding,
  type KintoneAdminCredentialSummaries,
  type KintoneAdminCredentialSummary,
  type AppBindingHealthCheckResponse,
  type PluginInstallationState,
} from "./hooks/useAppBindings";
import {
  KINTONE_API_TOKEN_REQUIRED_PERMISSIONS,
  buildKintoneApiTokenAdminUrl,
  buildKintoneHost,
  defaultAppBaseFolderForMode,
  getAppBindingModeLabel,
  getRecordFolderKindLabel,
  normalizeRequiredAppBaseFolder,
  parseKintoneAppUrl,
  type AppBindingModeId,
  type EditableFieldBinding,
  type ProbeItem,
  type RecordFolderLabelFieldCandidate,
} from "./app-binding-ux";
import { AppBindingStatusBadges } from "./app-binding-status-ui";
import { useKintoneEnvironment } from "./hooks/useKintoneEnvironment";
import { ClientCertificateExpiryBanner } from "./ClientCertificateExpiryBanner";
import { resolveRegisteredConnectionAccessUiState } from "./secure-access-ux";

function normalizeCandidateSubdomain(value: string | null) {
  if (!value) return "";
  const normalized = value.trim().toLowerCase().replace(/\.cybozu\.com$/, "");
  return /^[a-z0-9](?:[a-z0-9-]{1,61}[a-z0-9])$/.test(normalized)
    ? normalized
    : "";
}

export function hasPluginPresenceDrift(input: {
  pluginSetupStatus: AppBindingResponse["pluginSetupStatus"];
  healthResult: AppBindingHealthCheckResponse | null;
  pluginPublicationStarted: boolean;
}): boolean {
  if (
    input.pluginPublicationStarted ||
    input.healthResult?.connectivity.state !== "ok" ||
    input.healthResult.plugin.state !== "not_present"
  ) {
    return false;
  }

  return (
    input.pluginSetupStatus === "plugin_added_unlinked" ||
    input.pluginSetupStatus === "linked_reapply_pending" ||
    input.pluginSetupStatus === "reflected"
  );
}

function mapBindingToEditableField(
  binding: AppBindingResponse["targetFieldBindings"][number],
): EditableFieldBinding {
  return {
    selected: true,
    fieldBindingKey: binding.fieldBindingKey,
    fieldCode: binding.fieldCode,
    fieldLabel: binding.fieldCode,
    targetScopeKind: binding.targetScopeKind,
    subtableFieldCode: binding.subtableFieldCode ?? "",
    tableLabel: binding.subtableFieldCode ?? "",
    customAttachmentEnabled: true,
    thumbnailEnabled: binding.thumbnailSetting.enabled,
    thumbnailSizePx: binding.thumbnailSetting.sizePx
      ? String(binding.thumbnailSetting.sizePx)
      : "",
    importedFromKintoneAt: binding.thumbnailSetting.importedFromKintoneAt ?? null,
    importSourceState: binding.thumbnailSetting.importSourceState,
    fieldSourceStatus: "available",
  };
}

function hydrateEditableStateFromBinding(
  binding: AppBindingResponse,
  setters: {
    setStorageId: (value: string) => void;
    setModeId: (value: AppBindingModeId) => void;
    setAppBaseFolder: (value: string) => void;
    setHeaderLauncherEnabled: (value: boolean) => void;
    setReplaceNativeAttachmentFields: (value: boolean) => void;
    setShowHiddenStorageEntries: (value: boolean) => void;
    setMaxUploadFileBytes: (value: string) => void;
    setThumbnailSourceMaxBytes: (value: string) => void;
    setThumbnailSourceMaxPixels: (value: string) => void;
    setSharedStorageAccessMode: (value: SharedStorageAccessMode) => void;
    setCompatibleAttachmentRemovalPolicy: (value: "archive" | "delete") => void;
    setRecordFolderKind: (value: "record_id" | "record_label_field") => void;
    setRecordFolderLabelFieldBindingKey: (value: string) => void;
    setFieldBindings: (value: EditableFieldBinding[]) => void;
  },
) {
  setters.setStorageId(binding.storageId);
  setters.setModeId(binding.modeId);
  setters.setAppBaseFolder(binding.appBaseFolder ?? "");
  setters.setHeaderLauncherEnabled(binding.headerLauncherEnabled);
  setters.setReplaceNativeAttachmentFields(binding.replaceNativeAttachmentFields);
  setters.setShowHiddenStorageEntries(binding.showHiddenStorageEntries);
  setters.setMaxUploadFileBytes(binding.maxUploadFileBytes === null ? "" : String(binding.maxUploadFileBytes / 1024 ** 2));
  setters.setThumbnailSourceMaxBytes(
    String(
      binding.thumbnailSourceProfile?.maxSourceBytes ??
        DEFAULT_THUMBNAIL_SOURCE_MAX_BYTES,
    ),
  );
  setters.setThumbnailSourceMaxPixels(
    String(
      binding.thumbnailSourceProfile?.maxSourcePixels ??
        DEFAULT_THUMBNAIL_SOURCE_MAX_PIXELS,
    ),
  );
  setters.setCompatibleAttachmentRemovalPolicy(binding.compatibleAttachmentRemovalPolicy === "delete" ? "delete" : "archive");
  setters.setSharedStorageAccessMode(
    binding.sharedStorageAccessMode === "read_only" ? "read_only" : "read_write",
  );
  setters.setRecordFolderKind(binding.recordFolderKind ?? "record_id");
  setters.setRecordFolderLabelFieldBindingKey(
    binding.recordFolderLabelFieldBindingKey ?? "",
  );
  setters.setFieldBindings(binding.targetFieldBindings.map(mapBindingToEditableField));
}

function verifiedKintoneAdminCredential(
  credentials: KintoneAdminCredentialSummaries | null,
): KintoneAdminCredentialSummary | null {
  if (credentials?.shared?.credentialState === "verified") {
    return credentials.shared;
  }
  if (credentials?.personal?.credentialState === "verified") {
    return credentials.personal;
  }
  return null;
}

function pluginCorsStateKey(
  value: AppBindingResponse["pluginTransferPolicy"]["corsState"] | undefined,
) {
  switch (value) {
    case "cors_allowed":
      return "cors_allowed";
    case "cors_restricted":
      return "cors_restricted";
    case "cors_unknown":
    default:
      return "cors_unknown";
  }
}

function pluginUploadExecutorKey(
  value:
    | AppBindingResponse["pluginTransferPolicy"]["uploadExecutor"]
    | undefined,
) {
  return value === "kintone_proxy_upload"
    ? "kintone_proxy_upload"
    : "browser_direct_upload";
}

function normalizeFieldBindings(fieldBindings: EditableFieldBinding[]) {
  return fieldBindings
    .filter((binding) => binding.selected && binding.fieldCode.trim())
    .map((binding) => ({
      ...binding,
      fieldCode: binding.fieldCode.trim(),
      fieldLabel: binding.fieldLabel.trim(),
      subtableFieldCode:
        binding.targetScopeKind === "subtable_row"
          ? binding.subtableFieldCode.trim()
          : "",
      tableLabel:
        binding.targetScopeKind === "subtable_row"
          ? binding.tableLabel.trim()
          : "",
      thumbnailSizePx: binding.thumbnailEnabled
        ? normalizeFieldThumbnailSizeValue(binding.thumbnailSizePx)
        : "",
    }))
    .filter((binding) =>
      binding.targetScopeKind === "subtable_row"
        ? Boolean(binding.subtableFieldCode)
        : true,
    );
}

const FIELD_THUMBNAIL_SIZE_OPTIONS = ["50", "150", "250"] as const;
const DEFAULT_FIELD_THUMBNAIL_SIZE_OPTION = "150";

function readImportedThumbnailSizeOption(item: ProbeItem): string | null {
  if (item.sourceStatus === "source_unavailable" || item.thumbnailSize === null) {
    return null;
  }
  const value = String(item.thumbnailSize);
  return FIELD_THUMBNAIL_SIZE_OPTIONS.includes(
    value as (typeof FIELD_THUMBNAIL_SIZE_OPTIONS)[number],
  )
    ? value
    : null;
}

function readFieldSourceStatus(
  item: ProbeItem,
): EditableFieldBinding["fieldSourceStatus"] {
  if (item.fieldAvailability === "deleted") return "deleted";
  return "available";
}

function mergeImportedCandidates(
  current: EditableFieldBinding[],
  items: ProbeItem[],
  options: {
    selectNewCandidates?: boolean;
    overwriteExistingThumbnailSettings?: boolean;
  } = {},
): EditableFieldBinding[] {
  const selectNewCandidates = options.selectNewCandidates ?? false;
  const overwriteExistingThumbnailSettings =
    options.overwriteExistingThumbnailSettings ?? false;
  if (current.length === 0 || (current.length === 1 && !current[0]?.fieldCode.trim())) {
    return items.map((item) => {
      const importedThumbnailSize = readImportedThumbnailSizeOption(item);
      return {
        selected: selectNewCandidates,
        fieldBindingKey: item.fieldBindingKey,
        fieldCode: item.fieldCode,
        fieldLabel: item.fieldLabel,
        targetScopeKind: item.targetScopeKind,
        subtableFieldCode: item.subtableFieldCode ?? "",
        tableLabel: item.tableLabel ?? "",
        customAttachmentEnabled: true,
        thumbnailEnabled: importedThumbnailSize ? item.thumbnailEnabled : true,
        thumbnailSizePx:
          importedThumbnailSize ?? DEFAULT_FIELD_THUMBNAIL_SIZE_OPTION,
        importedFromKintoneAt: item.sourceUpdatedAt,
        importSourceState:
          item.sourceStatus === "source_unavailable"
            ? "source_unavailable"
            : "imported_default",
        fieldSourceStatus: readFieldSourceStatus(item),
      };
    });
  }

  const fieldTargetKey = (field: {
    targetScopeKind: "root_field" | "subtable_row";
    subtableFieldCode: string | null;
    fieldCode: string;
  }) => `${field.targetScopeKind}:${field.subtableFieldCode ?? ""}:${field.fieldCode}`;
  const next = [...current];
  for (const item of items) {
    const index = next.findIndex(
      (binding) =>
        binding.fieldBindingKey === item.fieldBindingKey ||
        (binding.fieldCode === item.fieldCode &&
          binding.targetScopeKind === item.targetScopeKind &&
          (binding.subtableFieldCode || null) === (item.subtableFieldCode ?? null)),
    );
    if (index >= 0) {
      if (item.fieldAvailability === "deleted") {
        next[index] = {
          ...next[index]!,
          fieldSourceStatus: "deleted",
        };
        continue;
      }
      const importedThumbnailSize = readImportedThumbnailSizeOption(item);
      const applyImportedThumbnailSetting =
        overwriteExistingThumbnailSettings && importedThumbnailSize !== null;
      next[index] = {
        ...next[index]!,
        fieldCode: item.fieldCode,
        fieldLabel: item.fieldLabel,
        targetScopeKind: item.targetScopeKind,
        subtableFieldCode: item.subtableFieldCode ?? "",
        tableLabel: item.tableLabel ?? next[index]!.tableLabel,
        thumbnailEnabled: applyImportedThumbnailSetting
          ? item.thumbnailEnabled
          : next[index]!.thumbnailEnabled,
        thumbnailSizePx: applyImportedThumbnailSetting
          ? importedThumbnailSize
          : next[index]!.thumbnailSizePx,
        importedFromKintoneAt: applyImportedThumbnailSetting
          ? item.sourceUpdatedAt
          : next[index]!.importedFromKintoneAt,
        importSourceState: applyImportedThumbnailSetting
          ? "reimported"
          : next[index]!.importSourceState,
        fieldSourceStatus: readFieldSourceStatus(item),
      };
      continue;
    }
    const importedThumbnailSize = readImportedThumbnailSizeOption(item);
    next.push({
      selected: selectNewCandidates,
      fieldBindingKey: item.fieldBindingKey,
      fieldCode: item.fieldCode,
      fieldLabel: item.fieldLabel,
      targetScopeKind: item.targetScopeKind,
      subtableFieldCode: item.subtableFieldCode ?? "",
      tableLabel: item.tableLabel ?? "",
      customAttachmentEnabled: true,
      thumbnailEnabled: importedThumbnailSize ? item.thumbnailEnabled : true,
      thumbnailSizePx:
        importedThumbnailSize ?? DEFAULT_FIELD_THUMBNAIL_SIZE_OPTION,
      importedFromKintoneAt: item.sourceUpdatedAt,
      importSourceState:
        item.sourceStatus === "source_unavailable"
          ? "source_unavailable"
          : "imported_default",
      fieldSourceStatus: readFieldSourceStatus(item),
    });
  }

  // Reorder to match the kintone form order conveyed by `items`.
  const itemOrder = new Map(items.map((item, index) => [fieldTargetKey(item), index]));
  return next
    .map((binding, index) => ({ binding, index }))
    .sort((left, right) => {
      const leftOrder = itemOrder.get(fieldTargetKey(left.binding));
      const rightOrder = itemOrder.get(fieldTargetKey(right.binding));
      if (leftOrder === undefined && rightOrder === undefined) {
        return left.index - right.index;
      }
      if (leftOrder === undefined) return 1;
      if (rightOrder === undefined) return -1;
      return leftOrder - rightOrder;
    })
    .map((entry) => entry.binding);
}

function describeWarning(code: string) {
  switch (code) {
    case "save_destination_change_requires_relocation":
      return "保存先が変更されています。外部ストレージ上の移設とプラグイン再反映が必要です。";
    default:
      return "設定内容の確認が必要です。アプリ設定と接続設定を確認してください。";
  }
}

type AppBindingStepKey = "target" | "storage" | "fields" | "save";
type SavedDetailTabKey = "settings" | "fields";
type SharedStorageAccessMode = "read_only" | "read_write";

function normalizeFieldThumbnailSizeValue(value: string) {
  const trimmed = value.trim();
  const numeric = Number(trimmed);
  return /^\d+$/.test(trimmed) && Number.isInteger(numeric) && numeric >= 1
    ? String(numeric)
    : DEFAULT_FIELD_THUMBNAIL_SIZE_OPTION;
}

function RuntimeCloudIcon({ className = "" }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      className={className}
      focusable="false"
    >
      <path
        d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96z"
        fill="currentColor"
      />
    </svg>
  );
}

function sharedStorageAccessModeKey(
  value: SharedStorageAccessMode | null | undefined,
) {
  return value === "read_only" ? "read_only" : "read_write";
}

const APP_BINDING_STEPS: Array<{ key: AppBindingStepKey; labelKey: string }> = [
  { key: "target", labelKey: "edit.steps.target" },
  { key: "storage", labelKey: "edit.steps.storage" },
  { key: "fields", labelKey: "edit.steps.fields" },
  { key: "save", labelKey: "edit.steps.save" },
];
const SAVED_DETAIL_TABS: Array<{ key: SavedDetailTabKey; labelKey: string }> = [
  { key: "settings", labelKey: "edit.savedTabs.settings" },
  { key: "fields", labelKey: "edit.savedTabs.fields" },
];
const DEFAULT_THUMBNAIL_SOURCE_MAX_BYTES = 10_000_000;
const DEFAULT_THUMBNAIL_SOURCE_MAX_PIXELS = 50_000_000;

function formatInteger(value: number | null | undefined) {
  return typeof value === "number" && Number.isFinite(value)
    ? value.toLocaleString("ja-JP")
    : "-";
}

function formatThumbnailSourceFormats(
  formats: readonly string[] | null | undefined,
) {
  return Array.isArray(formats) && formats.length > 0
    ? formats.map((format) => format.toUpperCase()).join(" / ")
    : "-";
}

function pluginLinkageLabel(binding: AppBindingResponse | null | undefined) {
  if (!binding) return null;
  return binding.activationState === "activated" ? "連携済" : "未連携";
}

function settingReflectionLabel(binding: AppBindingResponse | null | undefined) {
  if (!binding) return null;
  switch (binding.settingReflectionState) {
    case "reflected":
      return "反映済";
    case "plugin_reapply_pending":
      return "未反映あり";
    case "service_saved":
      return "未反映";
    default:
      return "-";
  }
}

function parseThumbnailSourceLimit(
  value: string,
  max: number,
): number | null {
  const trimmed = value.trim();
  if (!/^[0-9]+$/.test(trimmed)) {
    return null;
  }
  const parsed = Number.parseInt(trimmed, 10);
  return parsed >= 1 && parsed <= max ? parsed : null;
}

function StepNavigation({
  currentStep,
  onStepSelect,
  canSelectStep,
  showTargetStep = true,
}: {
  currentStep: AppBindingStepKey;
  onStepSelect?: (step: AppBindingStepKey) => void;
  canSelectStep?: (step: AppBindingStepKey) => boolean;
  showTargetStep?: boolean;
}) {
  const { t } = useTranslation("app-bindings");
  const visibleSteps = showTargetStep
    ? APP_BINDING_STEPS
    : APP_BINDING_STEPS.filter((step) => step.key !== "target");
  const currentIndex = visibleSteps.findIndex((step) => step.key === currentStep);

  return (
    <div
      aria-label="kintoneアプリタブ"
      className="mb-6 flex w-full gap-2 overflow-x-auto px-0.5 py-2"
    >
      {visibleSteps.map((step, index) => {
        const isActiveStep = index === currentIndex;
        const isComplete = index < currentIndex;
        const selectable = canSelectStep?.(step.key) ?? false;
        return (
          <button
            type="button"
            key={step.key}
            onClick={() => selectable && onStepSelect?.(step.key)}
            disabled={!selectable}
            className={[
              "min-w-[180px] flex-1 select-none rounded-lg border px-3 py-2 text-left text-sm shadow-sm transition-all duration-150",
              isActiveStep
                ? "border-primary bg-primary/10 font-medium text-primary shadow"
                : isComplete
                  ? "border-border bg-muted/40 text-foreground"
                  : "border-border bg-background text-muted-foreground",
              selectable
                ? "cursor-pointer hover:-translate-y-0.5 hover:border-primary hover:shadow active:translate-y-0 active:shadow-sm"
                : "cursor-default opacity-70 shadow-none",
            ].join(" ")}
          >
            <div>{t(step.labelKey)}</div>
          </button>
        );
      })}
    </div>
  );
}

function TargetAppSummary({
  subdomain,
  appId,
  modeLabel,
  appUrl,
  pluginLinkageLabel,
  settingReflectionLabel,
}: {
  subdomain?: string | null;
  appId: string;
  modeLabel?: string | null;
  appUrl?: string | null;
  pluginLinkageLabel?: string | null;
  settingReflectionLabel?: string | null;
}) {
  const domain = subdomain?.trim() || "-";
  const resolvedModeLabel = modeLabel?.trim() || "-";
  const resolvedAppUrl = appUrl?.trim() || "";
  return (
    <div
      role="group"
      aria-label="対象アプリの概要"
      className="mb-4 space-y-1 rounded-md border border-border bg-muted/20 px-3 py-2 text-sm leading-relaxed"
    >
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <span className="text-muted-foreground">kintoneサブドメイン: </span>
        <span className="font-medium">{domain}</span>
      </div>
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <span className="text-muted-foreground">アプリURL: </span>
        {resolvedAppUrl ? (
          <a
            href={resolvedAppUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex min-w-0 items-center gap-1 break-all font-mono text-xs font-medium text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            {resolvedAppUrl}
            <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
          </a>
        ) : (
          <span>-</span>
        )}
        <span className="text-muted-foreground">
          （アプリID <span className="font-mono text-foreground">{appId || "-"}</span>）
        </span>
      </div>
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <span className="text-muted-foreground">利用モード: </span>
        <span className="font-medium">{resolvedModeLabel}</span>
      </div>
      {pluginLinkageLabel || settingReflectionLabel ? (
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <span className="text-muted-foreground">プラグイン連携: </span>
          <span className="font-medium">{pluginLinkageLabel ?? "-"}</span>
          {settingReflectionLabel ? (
            <span className="text-muted-foreground">
              （設定反映:{" "}
              <span className="font-medium text-foreground">
                {settingReflectionLabel}
              </span>
              ）
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

// ──────────────────────────────────────────────
// New binding: app addition probe before shared settings tabs.
// ──────────────────────────────────────────────
function StepBadge({ children }: { children: ReactNode }) {
  return (
    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground">
      {children}
    </span>
  );
}

function NewBindingPhase1({
  currentKintoneSubdomain,
  currentCustomerId,
  connectionAccessKind,
  onRegisterInitialEnvironment,
  onProbeSuccess,
}: {
  currentKintoneSubdomain: string;
  currentCustomerId: string;
  connectionAccessKind: "standard" | "secure_access" | null;
  onRegisterInitialEnvironment?: (input: {
    kintoneAppUrl: string;
    apiToken: string;
  }) => Promise<void>;
  onProbeSuccess: (result: {
    guestSpaceId: string | null;
    kintoneApiToken: string;
    customerId: string;
    kintoneSubdomain: string;
    kintoneAppId: string;
    kintoneAppName: string;
    modeId: AppBindingModeId;
    probeItems: ProbeItem[];
    recordFolderLabelFieldCandidates: RecordFolderLabelFieldCandidate[];
  }) => void;
}) {
  const [appUrlInput, setAppUrlInput] = useState("");
  const [kintoneApiToken, setKintoneApiToken] = useState("");
  const [selectedModeId, setSelectedModeId] = useState<AppBindingModeId | "">("");
  const [initialRegistrationError, setInitialRegistrationError] =
    useState<string | null>(null);
  const [registrationPending, setRegistrationPending] = useState(false);
  const registrationPendingRef = useRef(false);
  // トークン入力 blur 時に自動で疎通確認した結果を保持する。
  // URL / トークンを編集すると無効化され、再度 blur で検証し直す（同一値は再検証しない）。
  const [verifiedResult, setVerifiedResult] = useState<{
    key: string;
    payload: {
      guestSpaceId: string | null;
      kintoneApiToken: string;
      customerId: string;
      kintoneSubdomain: string;
      kintoneAppId: string;
      kintoneAppName: string;
      probeItems: ProbeItem[];
      recordFolderLabelFieldCandidates: RecordFolderLabelFieldCandidate[];
    };
  } | null>(null);
  const { mutate: probeFields, isLoading, error, reset: resetProbeError } =
    useProbeKintoneAppFields();

  const parsedUrlState = useMemo(() => {
    const raw = appUrlInput.trim();
    if (!raw) return { parsed: null, error: null };
    try {
      return { parsed: parseKintoneAppUrl(raw), error: null };
    } catch (err) {
      return {
        parsed: null,
        error: err instanceof Error ? err.message : "URLが無効です。",
      };
    }
  }, [appUrlInput]);

  const parsed = parsedUrlState.parsed;
  const effectiveKintoneSubdomain = currentKintoneSubdomain;
  const environmentValidationError =
    parsed && parsed.subdomain !== currentKintoneSubdomain
      ? "組織に固定されたサブドメインとアプリ URL のサブドメインが一致しません。固定サブドメインのアプリ URL を入力してください。"
      : parsed && connectionAccessKind === null
        ? "kintoneへの接続方法を確認できません。再読み込みしてください。"
        : parsed && parsed.secureAccess !== (connectionAccessKind === "secure_access")
          ? "アプリ URL の接続先と選択した kintoneへの接続方法が一致しません。"
          : null;

  const verificationKey = useMemo(() => {
    const token = kintoneApiToken.trim();
    if (!parsed ||
        !token ||
        !selectedModeId ||
        !effectiveKintoneSubdomain ||
        environmentValidationError) {
      return null;
    }
    return [
      effectiveKintoneSubdomain,
      parsed.appId,
      parsed.guestSpaceId ?? "",
      selectedModeId,
      token,
    ].join("|");
  }, [
    effectiveKintoneSubdomain,
    environmentValidationError,
    kintoneApiToken,
    parsed,
    selectedModeId,
  ]);

  // URL 入力が解析できた時点で発行 / 確認画面の直リンクを生成する。
  const tokenAdminUrl = useMemo(
    () =>
      parsed
        ? buildKintoneApiTokenAdminUrl({
            subdomain: parsed.subdomain,
            appId: parsed.appId,
            guestSpaceId: parsed.guestSpaceId,
            secureAccess: parsed.secureAccess,
          })
        : null,
    [parsed],
  );

  const isVerified = Boolean(verifiedResult && verifiedResult.key === verificationKey);
  const showProbeError = Boolean(error) && !isVerified && !isLoading;

  function invalidateVerification() {
    if (verifiedResult) setVerifiedResult(null);
    if (error) resetProbeError();
    if (initialRegistrationError) setInitialRegistrationError(null);
  }

  async function handleVerifyToken() {
    if (!parsed || !verificationKey || !effectiveKintoneSubdomain) return;
    // 同一 URL + トークンで検証済みなら再検証しない（キャッシュ）。
    if (verifiedResult && verifiedResult.key === verificationKey) return;
    if (isLoading || registrationPendingRef.current) return;
    registrationPendingRef.current = true;
    setRegistrationPending(true);
    try {
      if (onRegisterInitialEnvironment) {
        await onRegisterInitialEnvironment({
          kintoneAppUrl: appUrlInput.trim(),
          apiToken: kintoneApiToken.trim(),
        });
      }
      const result = await probeFields({
        kintoneAppId: parsed.appId,
        guestSpaceId: parsed.guestSpaceId,
        kintoneApiToken: kintoneApiToken.trim(),
      });
      setVerifiedResult({
        key: verificationKey,
        payload: {
          guestSpaceId: parsed.guestSpaceId,
          kintoneApiToken: kintoneApiToken.trim(),
          customerId: currentCustomerId,
          kintoneSubdomain: result.kintoneSubdomain,
          kintoneAppId: result.kintoneAppId,
          kintoneAppName: result.kintoneAppName,
          probeItems: result.items,
          recordFolderLabelFieldCandidates: result.recordFolderLabelFieldCandidates,
        },
      });
    } catch (cause) {
      // hook が error を公開する
      if (onRegisterInitialEnvironment) {
        setInitialRegistrationError(
          apiErrorMessage(
            cause,
            "kintone環境を登録できませんでした。入力内容を確認して、もう一度お試しください。",
          ),
        );
      }
    } finally {
      registrationPendingRef.current = false;
      setRegistrationPending(false);
    }
  }

  const canProceed = Boolean(verifiedResult) && isVerified && Boolean(selectedModeId);

  function handleProceed() {
    if (!verifiedResult || !isVerified || !selectedModeId) return;
    onProbeSuccess({ ...verifiedResult.payload, modeId: selectedModeId });
  }

  return (
    <Card>
      <CardHeader className="space-y-1.5">
        <CardTitle className="text-base">
          kintoneアプリの添付ファイルを外部ストレージに保存する設定
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          このアプリの添付ファイルフィールドのファイル実体を、指定したストレージへ自動保存します。kintone
          の画面はそのまま使えます。
        </p>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="rounded-md border bg-muted/20 px-3 py-2 text-sm">
          <span className="text-muted-foreground">サブドメイン: </span>
          <span className="font-mono">{currentKintoneSubdomain}</span>
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <StepBadge>1</StepBadge>
            <Label htmlFor="appUrl">kintoneアプリ URL</Label>
          </div>
          <Input
            id="appUrl"
            value={appUrlInput}
            disabled={registrationPending}
            onChange={(e) => {
              setAppUrlInput(e.target.value);
              invalidateVerification();
            }}
            placeholder="https://example.cybozu.com/k/1/"
          />
          {parsedUrlState.error ? (
            <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              {parsedUrlState.error}
            </div>
          ) : parsed ? (
            <div className="space-y-2 rounded-lg border border-border bg-muted/20 p-3 text-sm">
              <div>
                <span className="text-muted-foreground">サブドメイン:</span>{" "}
                <span className="font-mono">{parsed.subdomain}</span>
                {"　"}
                <span className="text-muted-foreground">アプリ ID:</span>{" "}
                <span className="font-mono">{parsed.appId}</span>
              </div>
              {tokenAdminUrl ? (
                <div className="space-y-1.5 border-t border-border/60 pt-2">
                  <a
                    href={tokenAdminUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 font-medium text-primary underline-offset-2 hover:underline"
                  >
                    このアプリの API トークンを発行 / 確認する
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                    <span>必要な権限:</span>
                    {KINTONE_API_TOKEN_REQUIRED_PERMISSIONS.map((permission) => (
                      <span key={permission} className="inline-flex items-center gap-1">
                        <Check className="h-3 w-3 text-primary" />
                        {permission}
                      </span>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className="space-y-2">
          <AppBindingModeSelector
            legendPrefix={<StepBadge>2</StepBadge>}
            value={selectedModeId ?? ""}
            disabled={registrationPending}
            onChange={(mode) => {
              setSelectedModeId(mode);
              invalidateVerification();
            }}
          />
          <p className="text-xs text-muted-foreground">
            利用モードは作成後に変更できません。
          </p>
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <StepBadge>3</StepBadge>
            <Label htmlFor="apiToken">kintone API トークン</Label>
          </div>
          <Input
            id="apiToken"
            type="password"
            value={kintoneApiToken}
            disabled={!selectedModeId || connectionAccessKind === null || registrationPending}
            onChange={(e) => {
              setKintoneApiToken(e.target.value);
              invalidateVerification();
            }}
            onBlur={() => void handleVerifyToken()}
            placeholder={connectionAccessKind === null ? "接続方法の確認をお待ちください" : selectedModeId ? "API トークンを入力" : "先に利用モードを選択してください"}
            autoComplete="off"
          />
          {isLoading || registrationPending ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              {registrationPending ? "kintone環境を登録しています..." : "疎通確認中..."}
            </div>
          ) : isVerified ? (
            <div className="flex items-center gap-2 text-sm font-medium text-emerald-600">
              <CheckCircle2 className="h-4 w-4" />
              疎通確認 OK（アプリ名: {verifiedResult?.payload.kintoneAppName || "—"}）
            </div>
          ) : environmentValidationError || initialRegistrationError || showProbeError ? (
            <div className="space-y-1 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              <div className="flex items-center gap-2 font-medium">
                <AlertTriangle className="h-4 w-4" />
                {environmentValidationError ?? initialRegistrationError ?? error}
              </div>
              <p className="text-xs">
                kintone でトークンの権限（アプリ管理 / レコード閲覧 / レコード編集）を確認し、
                トークンを更新したら「アプリを更新」してから、もう一度お試しください。
              </p>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">
              利用モードを選択してから入力してください。入力後に自動で疎通確認します。トークンは確認時には保存されず、設定保存時に暗号化して保存します。
            </p>
          )}
        </div>

        <Button onClick={handleProceed} disabled={!canProceed || registrationPending}>
          次へ：フィールド設定
          <ArrowRight className="ml-1 h-4 w-4" />
        </Button>
      </CardContent>
    </Card>
  );
}

// ──────────────────────────────────────────────
// Shared: field bindings editor
// ──────────────────────────────────────────────
function FieldBindingsEditor({
  fieldBindings,
  effectiveKintoneAppId,
  isImporting,
  readOnly,
  onImport,
  onSelectAll,
  onClearSelection,
  onUpdate,
}: {
  fieldBindings: EditableFieldBinding[];
  effectiveKintoneAppId: string;
  isImporting: boolean;
  readOnly: boolean;
  onImport: () => void;
  onSelectAll: () => void;
  onClearSelection: () => void;
  onUpdate: (
    key: string,
    updater: (current: EditableFieldBinding) => EditableFieldBinding,
  ) => void;
}) {
  const { t } = useTranslation("app-bindings");
  const selectedCount = normalizeFieldBindings(fieldBindings).length;

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <div className="font-medium">{t("edit.fields")}</div>
          <p className="text-xs text-muted-foreground">
            フィールドごとに標準添付と外部添付を切り替えます。
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onSelectAll}
            disabled={readOnly || fieldBindings.length === 0}
          >
            すべて外部添付にする
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onClearSelection}
            disabled={readOnly || fieldBindings.length === 0}
          >
            すべて標準添付に戻す
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onImport}
            disabled={readOnly || !effectiveKintoneAppId || isImporting}
          >
            <RefreshCcw className="mr-2 h-4 w-4" />
            kintone 設定を取り込む
          </Button>
        </div>
      </div>
      <div className="text-xs text-muted-foreground">
        外部添付: {selectedCount} 件
      </div>

      <div className="space-y-3">
        {fieldBindings.length === 0 ? (
          <div className="rounded-md border border-dashed border-border p-4 text-sm text-muted-foreground">
            対象にできる添付フィールド候補がありません。kintone 側のフィールド設定と API トークン権限を確認してください。
          </div>
        ) : null}
        {fieldBindings.map((binding, index) => {
          const fieldName =
            binding.fieldLabel ||
            binding.fieldCode ||
            t("edit.fieldRowLabel", { index: index + 1 });
          const scopeLabel =
            binding.targetScopeKind === "subtable_row"
              ? `テーブル: ${binding.tableLabel || binding.subtableFieldCode || "-"}`
              : null;
          const thumbnailSelectValue = binding.thumbnailEnabled
            ? normalizeFieldThumbnailSizeValue(binding.thumbnailSizePx)
            : "none";
          const hasPreservedUnsupportedThumbnailSize =
            binding.thumbnailEnabled &&
            !FIELD_THUMBNAIL_SIZE_OPTIONS.includes(
              thumbnailSelectValue as (typeof FIELD_THUMBNAIL_SIZE_OPTIONS)[number],
            );
          const thumbnailSelectId = `thumbnail-size-${binding.fieldBindingKey.replace(/[^a-zA-Z0-9_-]/g, "-")}`;

          return (
            <div
              key={binding.fieldBindingKey}
              className={`rounded-lg border p-4 transition-colors ${
                binding.selected
                  ? "border-primary/35 bg-background shadow-sm"
                  : "border-border bg-muted/25"
              }`}
            >
              <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                <div className="flex min-w-0 items-start gap-3">
                  <div
                    className={`mt-0.5 flex h-14 w-14 shrink-0 flex-col items-center justify-center gap-1 rounded-md border text-[11px] font-medium leading-none ${
                      binding.selected
                        ? "border-[#3498db]/30 bg-[#3498db]/10 text-[#3498db]"
                        : "border-border bg-background text-muted-foreground"
                    }`}
                    aria-hidden="true"
                  >
                    {binding.selected ? (
                      <RuntimeCloudIcon className="h-[18px] w-[18px]" />
                    ) : (
                      <Paperclip className="h-5 w-5" />
                    )}
                    <span>{binding.selected ? "外部" : "標準"}</span>
                  </div>
                  <div className="min-w-0 space-y-1">
                    <div className="truncate font-medium">{fieldName}</div>
                    {scopeLabel ? (
                      <div className="text-xs text-muted-foreground">
                        {scopeLabel}
                      </div>
                    ) : null}
                    <div className="text-xs text-muted-foreground">
                      コード:{" "}
                      <span className="font-mono">
                        {binding.fieldCode || "-"}
                      </span>
                    </div>
                    {binding.fieldSourceStatus === "deleted" ? (
                      <div className="pt-1">
                        <span className="inline-flex rounded-full border border-amber-300 bg-amber-50 px-2 py-1 text-xs text-amber-900">
                          kintone 側で現在見つかりません。設定を残す場合は明示値として保存されます。
                        </span>
                      </div>
                    ) : null}
                  </div>
                </div>
                <div className="flex shrink-0 items-start md:justify-end">
                  <Button
                    type="button"
                    size="sm"
                    variant={binding.selected ? "outline" : "default"}
                    disabled={readOnly}
                    aria-pressed={binding.selected}
                    aria-label={`${fieldName}を${binding.selected ? "標準添付に戻す" : "外部添付にする"}`}
                    onClick={() =>
                      onUpdate(binding.fieldBindingKey, (current) => ({
                        ...current,
                        selected: !current.selected,
                      }))
                    }
                  >
                    {binding.selected ? "標準添付に戻す" : "外部添付にする"}
                  </Button>
                </div>
              </div>

              {binding.selected ? (
                <div className="mt-4 border-t border-border pt-4">
                  <div className="flex max-w-sm flex-col gap-2 sm:flex-row sm:items-center">
                    <Label htmlFor={thumbnailSelectId} className="shrink-0">
                      サムネイルサイズ
                    </Label>
                    <select
                      id={thumbnailSelectId}
                      aria-label={`${fieldName} サムネイルサイズ`}
                      value={thumbnailSelectValue}
                      disabled={readOnly}
                      onChange={(event) => {
                        const value = event.target.value;
                        onUpdate(binding.fieldBindingKey, (current) => ({
                          ...current,
                          thumbnailEnabled: value !== "none",
                          thumbnailSizePx: value === "none" ? "" : value,
                          importSourceState: "user_overridden",
                        }));
                      }}
                      className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-60 sm:w-36"
                    >
                      <option value="none">表示しない</option>
                      {hasPreservedUnsupportedThumbnailSize ? (
                        <option value={thumbnailSelectValue} disabled>
                          {thumbnailSelectValue} px（保存済み値を保持）
                        </option>
                      ) : null}
                      {FIELD_THUMBNAIL_SIZE_OPTIONS.map((size) => (
                        <option key={size} value={size}>
                          {size} px
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function savedDetailTabTitleKey(tab: SavedDetailTabKey) {
  return (
    SAVED_DETAIL_TABS.find((item) => item.key === tab)?.labelKey ??
    "edit.savedTabs.settings"
  );
}

function savedDetailTabEditStep(tab: SavedDetailTabKey): AppBindingStepKey {
  return tab === "fields" ? "fields" : "storage";
}

// ──────────────────────────────────────────────
// Main page
// ──────────────────────────────────────────────
export function AppBindingEditPage() {
  const { t } = useTranslation("app-bindings");
  const navigate = useNavigate();
  const location = useLocation();
  const { user, refreshMe } = useAuth();
  const isCustomerAdmin = user?.currentMembership?.role === "customer_admin";
  const initialSetupSession = useInitialKintoneSetupSession();
  const {
    environment: currentEnvironment,
    isLoading: currentEnvironmentLoading,
    error: currentEnvironmentError,
    refresh: refreshCurrentEnvironment,
  } = useKintoneEnvironment();
  const {
    view: registeredSecureAccessView,
    isLoading: isRegisteredSecureAccessLoading,
    error: registeredSecureAccessError,
    refresh: refreshRegisteredSecureAccess,
  } = useSecureAccessSettings({
    enabled: Boolean(
      user?.serviceSubscription?.kintoneSubdomain ||
      currentEnvironment?.kintoneSubdomain
    ),
  });
  const { kintoneAppId: routeKintoneAppId } = useParams<{
    kintoneAppId?: string;
  }>();
  const isEditing = Boolean(routeKintoneAppId);
  const routeState = location.state as
    | {
        justSaved?: boolean;
        savedBinding?: AppBindingResponse;
        continueInitialSetupAt?: "fields";
      }
    | null;
  const searchParams = new URLSearchParams(location.search);
  const requestedCandidateSubdomain =
    searchParams.get("candidateSubdomain");
  const candidateKintoneSubdomain =
    normalizeCandidateSubdomain(requestedCandidateSubdomain);
  const currentKintoneSubdomain =
    user?.serviceSubscription?.kintoneSubdomain ??
    currentEnvironment?.kintoneSubdomain ??
    "";
  const onboardingKintoneSubdomain =
    currentKintoneSubdomain || candidateKintoneSubdomain;
  const currentCustomerId = user?.serviceSubscription?.customerId ?? "";
  const initialConnectionAccess: KintoneConnectionAccess | null =
    currentKintoneSubdomain
      ? null
      : initialSetupSession.connectionAccessFor(
          currentCustomerId,
          candidateKintoneSubdomain,
        );
  const stateSavedBinding =
    routeState?.savedBinding &&
    (!routeKintoneAppId || routeState.savedBinding.kintoneAppId === routeKintoneAppId)
      ? routeState.savedBinding
      : null;
  const stateJustSaved = Boolean(routeState?.justSaved && stateSavedBinding);
  const continueInitialSetupAt = routeState?.continueInitialSetupAt ?? null;
  const requestedSetupMethod = searchParams.get("setupMethod");
  const [phase, setPhase] = useState<1 | 2>(1);
  const [setupMethod, setSetupMethod] = useState<"manual" | "easy">(
    requestedSetupMethod === "manual" ? "manual" : "easy",
  );
  const [initialEnvironmentRegistered, setInitialEnvironmentRegistered] =
    useState(false);
  const [initialEnvironmentRegistrationPending, setInitialEnvironmentRegistrationPending] =
    useState(false);
  const [completedInitialConnectionAccessKind, setCompletedInitialConnectionAccessKind] =
    useState<"standard" | "secure_access" | null>(null);
  const connectionAccessKindForLinks: "standard" | "secure_access" | null =
    currentKintoneSubdomain
      ? registeredSecureAccessView
        ? registeredSecureAccessView.connectionKind
        : null
      : initialConnectionAccess?.kind ?? completedInitialConnectionAccessKind;
  const registeredConnectionAccessUiState =
    resolveRegisteredConnectionAccessUiState({
      hasRegisteredEnvironment: Boolean(currentKintoneSubdomain),
      isLoading: isRegisteredSecureAccessLoading,
      hasReadError: Boolean(registeredSecureAccessError),
      connectionKind: registeredSecureAccessView?.connectionKind ?? null,
      configurationState:
        registeredSecureAccessView?.configurationState ?? null,
      expiresAt: registeredSecureAccessView?.expiresAt ?? null,
    });
  const isKintoneDependentActionBlocked =
    registeredConnectionAccessUiState !== "not_applicable" &&
    registeredConnectionAccessUiState !== "available";
  const initialRegistrationCommandRef = useRef<{
    fingerprint: string;
    idempotencyKey: string;
    body: InitialEnvironmentRegistrationRequest;
  } | null>(null);
  const { headlessSetupEnabled } = useFeatures();
  const isEasySetupFeatureEnabled = headlessSetupEnabled;
  const easySetupUnavailableReason = !headlessSetupEnabled
      ? "簡単セットアップは準備中です。"
      : undefined;
  const {
    credentials: setupCredentials,
  } = useKintoneAdminCredentials({
    enabled: Boolean(currentKintoneSubdomain),
  });
  const setupVerifiedCredential = verifiedKintoneAdminCredential(setupCredentials);
  const hasVerifiedSetupCredential = Boolean(setupVerifiedCredential);
  const [probeResult, setProbeResult] = useState<{
    guestSpaceId: string | null;
    kintoneApiToken: string;
    customerId: string;
    kintoneSubdomain: string;
    kintoneAppId: string;
    kintoneAppName: string;
    modeId: AppBindingModeId;
    probeItems: ProbeItem[];
    recordFolderLabelFieldCandidates: RecordFolderLabelFieldCandidate[];
  } | null>(null);
  const [wizardStep, setWizardStep] = useState<AppBindingStepKey>(
    isEditing ? "storage" : "target",
  );

  // Config form state
  const [storageId, setStorageId] = useState("");
  const [modeId, setModeId] = useState<AppBindingModeId>("attachment_compatible");
  const [compatibleAttachmentRemovalPolicy, setCompatibleAttachmentRemovalPolicy] = useState<"archive" | "delete">("archive");
  const [sharedStorageAccessMode, setSharedStorageAccessMode] =
    useState<SharedStorageAccessMode>("read_write");
  const [appBaseFolder, setAppBaseFolder] = useState("");
  const [fieldBindings, setFieldBindings] = useState<EditableFieldBinding[]>([]);
  const [headerLauncherEnabled, setHeaderLauncherEnabled] = useState(true);
  const [replaceNativeAttachmentFields, setReplaceNativeAttachmentFields] =
    useState(true);
  const [showHiddenStorageEntries, setShowHiddenStorageEntries] = useState(false);
  const [maxUploadFileBytes, setMaxUploadFileBytes] = useState("");
  const [thumbnailSourceMaxBytes, setThumbnailSourceMaxBytes] = useState(
    String(DEFAULT_THUMBNAIL_SOURCE_MAX_BYTES),
  );
  const [thumbnailSourceMaxPixels, setThumbnailSourceMaxPixels] = useState(
    String(DEFAULT_THUMBNAIL_SOURCE_MAX_PIXELS),
  );
  const [recordFolderKind, setRecordFolderKind] = useState<
    "record_id" | "record_label_field"
  >("record_id");
  const [recordFolderLabelFieldBindingKey, setRecordFolderLabelFieldBindingKey] =
    useState("");
  const [appNameOverride, setAppNameOverride] = useState<string | null>(null);
  const [pluginInstallState, setPluginInstallState] =
    useState<PluginInstallationState | null>(null);
  const [pluginPublishConfirmationOpen, setPluginPublishConfirmationOpen] =
    useState(false);
  const [
    pluginLinkageDisconnectConfirmationOpen,
    setPluginLinkageDisconnectConfirmationOpen,
  ] = useState(false);
  const autoPluginInstallAttemptedRef = useRef<Set<string>>(new Set());
  const healthCheckRanRef = useRef<string | null>(null);
  const initialSetupFieldContinuationActiveRef = useRef(false);
  const hydratedBindingIdRef = useRef<string | null>(null);
  const [repairEasySetup, setRepairEasySetup] = useState(false);
  const [pluginManualSetupOpen, setPluginManualSetupOpen] = useState(false);
  const [editTokenOpen, setEditTokenOpen] = useState(false);
  const [kintoneApiToken, setKintoneApiToken] = useState("");
  const [statusMessage, setStatusMessage] = useState<string | { kind: "saved" } | null>(
    stateJustSaved ? { kind: "saved" } : null,
  );
  const [savedView, setSavedView] = useState(stateJustSaved);
  const [savedDetailTab, setSavedDetailTab] = useState<SavedDetailTabKey>("settings");
  const [alignmentSectionOpen, setAlignmentSectionOpen] = useState(location.hash === "#attachment-storage-alignment");
  const [restoreSectionOpen, setRestoreSectionOpen] = useState(location.hash === "#attachment-environment-restore");
  const [exportSectionOpen, setExportSectionOpen] = useState(false);
  const [alignmentStatus, setAlignmentStatus] = useState<MaintenanceOperationStatus | null>(null);
  const [restoreStatus, setRestoreStatus] = useState<MaintenanceOperationStatus | null>(null);
  const [orphanSectionOpen, setOrphanSectionOpen] = useState(false);
  const [deleteAppBindingConfirmation, setDeleteAppBindingConfirmation] =
    useState<{ force: boolean } | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [lastReflection, setLastReflection] = useState<{
    handoffToken: string;
    handoffExpiresAt: string;
  } | null>(null);

  const effectiveKintoneAppId =
    routeKintoneAppId ??
    probeResult?.kintoneAppId ??
    stateSavedBinding?.kintoneAppId ??
    "";
  const effectiveKintoneSubdomain = currentKintoneSubdomain;
  const extractedSubdomain = currentKintoneSubdomain;

  const openAttachmentStorageAlignment = useCallback(() => {
    setAlignmentSectionOpen(true);
    setRestoreSectionOpen(false);
  }, []);

  const openAttachmentEnvironmentRestore = useCallback(() => {
    setRestoreSectionOpen(true);
    setAlignmentSectionOpen(false);
    setExportSectionOpen(true);
  }, []);

  const { binding, isLoading: isBindingLoading, error: bindingError, refresh } =
    useAppBindingDetail(routeKintoneAppId ?? "");
  const hydratedBinding = binding ?? stateSavedBinding;
  const visibleStatusMessage = statusMessage !== null && typeof statusMessage !== "string"
    ? hydratedBinding?.settingReflectionState === "plugin_reapply_pending"
      ? "保存しました。kintone プラグイン設定画面で再反映してください。トークン再発行は不要です。"
      : hydratedBinding?.settingReflectionState === "reflected"
        ? "保存しました。設定は反映済みです。"
        : "保存しました。設定反映状態を確認してください。"
    : statusMessage;

  const automaticAlignment = useAutomaticAttachmentAlignment(hydratedBinding?.kintoneAppId ?? "", hydratedBinding?.appBindingId ?? "");
  useEffect(() => { setAlignmentStatus(null); setRestoreStatus(null); }, [currentCustomerId, hydratedBinding?.appBindingId, savedView]);
  useEffect(() => {
    if (location.hash === "#attachment-storage-alignment") { setRestoreSectionOpen(false); setAlignmentSectionOpen(true); }
    if (location.hash === "#attachment-environment-restore") { setAlignmentSectionOpen(false); setRestoreSectionOpen(true); }
  }, [location.hash]);
  const maintenancePending = Boolean(alignmentStatus?.pending || restoreStatus?.pending);
  const manualAlignment = maintenancePending ? {
    ...automaticAlignment,
    canStartManual: false,
    state: automaticAlignment.state ? {
      ...automaticAlignment.state,
      manualExecutionBlockers: [...automaticAlignment.state.manualExecutionBlockers, {
        code: "management_execution_active" as const,
        message: "管理操作の受付結果を確認中です。完了するまでお待ちください。",
        requiredAction: "refresh_state" as const,
      }],
    } : null,
  } : automaticAlignment;
  const refreshAutomaticAlignment = automaticAlignment.refresh;
  useEffect(() => { void refreshAutomaticAlignment(); }, [hydratedBinding?.updatedAt, hydratedBinding?.latestSettingsRevision, refreshAutomaticAlignment]);
  const statusSummaryBinding = isEditing ? hydratedBinding : null;
  // Guest-space apps put resources under a /guest/{spaceId} path prefix. Derive
  // the guest space id from the probe (new binding) or the saved binding's
  // space metadata (kintoneSpaceKind/kintoneSpaceId) for kintone deep-links.
  const effectiveGuestSpaceId =
    probeResult?.guestSpaceId ??
    (hydratedBinding?.kintoneSpaceKind === "guest_space"
      ? hydratedBinding?.kintoneSpaceId ?? null
      : null);
  const kintoneGuestSegment = effectiveGuestSpaceId
    ? `/guest/${encodeURIComponent(effectiveGuestSpaceId)}`
    : "";
  const effectiveKintoneHost = connectionAccessKindForLinks
    ? buildKintoneHost(
        effectiveKintoneSubdomain,
        connectionAccessKindForLinks === "secure_access",
      )
    : null;
  const kintoneKBase =
    effectiveKintoneHost && effectiveKintoneAppId
      ? `https://${effectiveKintoneHost}/k${kintoneGuestSegment}`
      : null;
  const currentKintoneAppUrl = kintoneKBase
    ? `${kintoneKBase}/${effectiveKintoneAppId}/`
    : "";
  const apiTokenAdminUrl = kintoneKBase
    ? `${kintoneKBase}/admin/app/apitoken?app=${effectiveKintoneAppId}`
    : null;
  const effectiveKintoneAppName =
    appNameOverride ??
    hydratedBinding?.kintoneAppName ??
    probeResult?.kintoneAppName ??
    "";
  const { mutate: upsertBinding, isLoading: isSaving, error: upsertError, errorCode: saveErrorCode } =
    useUpsertAppBinding();
  const appLimitReached = saveErrorCode === "app_limit_reached";
  const saveError = appLimitReached ? null : upsertError;
  const { mutate: importFieldSettings, isLoading: isImporting, error: importError } =
    useImportFieldSettings(effectiveKintoneAppId);
  const applyImportedFieldSettings = useCallback(
    (
      result: Awaited<ReturnType<typeof importFieldSettings>>,
      options: {
        overwriteExistingThumbnailSettings?: boolean;
      } = {},
    ) => {
      setFieldBindings((current) =>
        mergeImportedCandidates(current, result.items, {
          selectNewCandidates: false,
          overwriteExistingThumbnailSettings:
            options.overwriteExistingThumbnailSettings,
        }),
      );
      if (result.kintoneAppName) {
        setAppNameOverride(result.kintoneAppName);
      }
    },
    [],
  );
  const importSavedFieldSettingsForEditing = useCallback(
    async (savedBinding: AppBindingResponse) => {
      const targets = savedBinding.targetFieldBindings.map((binding) => ({
        fieldBindingKey: binding.fieldBindingKey,
        fieldCode: binding.fieldCode,
        targetScopeKind: binding.targetScopeKind,
        subtableFieldCode:
          binding.targetScopeKind === "subtable_row"
            ? binding.subtableFieldCode
            : null,
      }));
      const result = await importFieldSettings(targets.length ? targets : undefined);
      applyImportedFieldSettings(result);
    },
    [applyImportedFieldSettings, importFieldSettings],
  );
  const {
    mutate: ensurePluginInstallation,
    isLoading: isEnsuringPluginInstallation,
    error: pluginInstallationError,
  } = useEnsurePluginInstallation(effectiveKintoneAppId);
  const {
    run: runHealthCheck,
    result: healthResult,
    isLoading: isHealthChecking,
    error: healthError,
  } = useHealthCheck(effectiveKintoneAppId);
  const {
    mutate: requestReflection,
    isLoading: isReflecting,
    error: reflectionError,
  } = useRequestReflection(effectiveKintoneAppId);
  const {
    mutate: disconnectPluginLinkage,
    isLoading: isDisconnectingPluginLinkage,
    error: disconnectPluginLinkageError,
  } = useDisconnectPluginLinkage(effectiveKintoneAppId);
  const {
    mutate: deleteAppBinding,
    isLoading: isDeletingAppBinding,
    error: deleteAppBindingError,
  } = useDeleteAppBinding(effectiveKintoneAppId);
  const { items: storages, isLoading: isStorageLoading, error: storageError } =
    useStorageList({ limit: "100" });

  const readyStorages = useMemo(
    () => storages.filter(isStorageIntegratedAvailable),
    [storages],
  );
  const selectedStorage =
    readyStorages.find((s) => s.storageId === storageId) ?? null;
  const savedStorage =
    storages.find((s) => s.storageId === hydratedBinding?.storageId) ?? null;
  const alignmentAvailable = Boolean(
    hydratedBinding?.modeId && hydratedBinding.storageId,
  );
  const reflectionAvailable = Boolean(
    hydratedBinding?.modeId && hydratedBinding.storageId,
  );
  const currentWarnings = binding?.configurationWarnings ?? [];
  const configurationWarningBanner = currentWarnings.length > 0 ? (
    <div role="alert" className="mb-4 space-y-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
      {currentWarnings.map((warning) => <p key={warning}>{describeWarning(warning)}</p>)}
    </div>
  ) : null;
  const pluginLinkageActivated = hydratedBinding?.activationState === "activated";
  // kintone plugin settings page for this app. Reflection (config write) can
  // only be performed by a kintone admin in this screen, so we deep-link to it.
  const pluginSettingsUrl = kintoneKBase
    ? `${kintoneKBase}/admin/app/${effectiveKintoneAppId}/plugin/`
    : null;
  const hasSavedBinding = Boolean(hydratedBinding);
  const savedDetailAppBaseFolder = hydratedBinding?.appBaseFolder ?? appBaseFolder ?? "";
  const savedDetailAppBaseFolderMissing = hasSavedBinding && !savedDetailAppBaseFolder.trim();
  // kintone system-wide plugin registry. Registering (uploading) the plugin
  // file is a system-admin action; there is no API to do it server-side.
  const systemPluginUrl = effectiveKintoneHost
    ? `https://${effectiveKintoneHost}/k/admin/system/plugin/`
    : null;
  const pluginPackageHref = consoleBffRequestUrl("/console-bff/plugin-package");
  const pluginLinkClass =
    "inline-flex items-center gap-1.5 rounded-md border border-amber-400 bg-background px-3 py-1.5 text-sm font-medium text-amber-900 shadow-sm transition-all duration-150 hover:-translate-y-0.5 hover:shadow active:translate-y-0 active:shadow-sm";
  const pluginManualSetupButton = (
    <Button
      type="button"
      size="sm"
      variant="outline"
      aria-expanded={pluginManualSetupOpen}
      onClick={() => setPluginManualSetupOpen((current) => !current)}
    >
      <Wrench className="mr-1 h-3.5 w-3.5" />
      手動セットアップ
    </Button>
  );
  const pluginSetupStatus =
    hydratedBinding?.pluginSetupStatus ?? "plugin_installation_unverified";
  const pluginNotAdded =
    hasSavedBinding && pluginSetupStatus === "plugin_not_added";
  const pluginInstallationNeedsAction =
    hasSavedBinding && pluginSetupStatus === "plugin_installation_action_required";
  const pluginInstallationChecking =
    hasSavedBinding && pluginSetupStatus === "plugin_installation_unverified";
  const pluginInstallationFailureReason =
    hydratedBinding?.kintonePluginInstallationFailureReason;
  const pluginPublicationStarted = pluginInstallState === "added_and_deployed";
  const pluginPresenceDrift = hasPluginPresenceDrift({
    pluginSetupStatus,
    healthResult,
    pluginPublicationStarted,
  });
  const needsActivation =
    hasSavedBinding && pluginSetupStatus === "plugin_added_unlinked";
  // 簡単セットアップの初期 app binding は mode/保存先を作成時点で保持する。
  // 対象 field 0 件でも plugin_reapply_pending なら app-level replica の再反映は必要。
  const needsReflection =
    hasSavedBinding &&
    pluginSetupStatus === "linked_reapply_pending" &&
    reflectionAvailable;
  // The plugin-add check runs ONLY with a saved API token (the auto-ensure effect
  // bails without one). So without a token there is nothing to show — the health
  // banner's "保存済みトークンが必要" already directs the user to easy setup /
  // manual edit. Gating here avoids a contradictory second warning that claims to
  // be "確認しています" while the check can never run (DES-SETUP-020).
  const hasConfiguredToken = Boolean(hydratedBinding?.kintoneApiTokenConfigured);
  const pluginActionRequired =
    hasConfiguredToken &&
    (pluginNotAdded ||
      pluginInstallationNeedsAction ||
      pluginInstallationChecking ||
      needsActivation ||
      needsReflection);
  const pluginActionTitle = pluginNotAdded
    ? "kintoneアプリへプラグインを公開してください"
    : pluginInstallationNeedsAction
      ? "kintone プラグイン追加に対応が必要です"
        : pluginInstallationChecking
          ? "kintone プラグイン追加状態を確認しています"
        : needsActivation
          ? "kintone プラグイン連携が必要です"
          : "保存した設定が kintone 側へ未反映です";
  const connectionAccessStatusBanner =
    registeredConnectionAccessUiState === "loading" ? (
      <section
        role="status"
        aria-live="polite"
        className="mb-5 max-w-3xl rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm"
      >
        <p className="font-semibold text-blue-900">
          kintone接続設定を確認しています
        </p>
        <p className="mt-1 text-blue-800">
          確認が完了すると、kintoneへの確認や設定編集を開始できます。
        </p>
      </section>
    ) : registeredConnectionAccessUiState === "connection_unavailable" ? (
      <section
        role="alert"
        className="mb-5 max-w-3xl rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm"
      >
        <p className="font-semibold text-red-900">
          kintone接続設定を確認できません
        </p>
        <p className="mt-1 text-red-800">
          接続設定を確認できるまで、kintoneへの確認や設定編集は開始しません。
        </p>
        <Button
          type="button"
          className="mt-3"
          variant="outline"
          onClick={() => void refreshRegisteredSecureAccess()}
        >
          再読み込み
        </Button>
      </section>
    ) : registeredConnectionAccessUiState === "certificate_expired" ? (
      <ClientCertificateExpiryBanner
        onOpenSettings={
          isCustomerAdmin
            ? () => navigate("/console/service-subscription")
            : undefined
        }
      />
    ) : null;
  const combineVersionAndReflection =
    pluginActionRequired &&
    needsReflection &&
    !pluginPresenceDrift &&
    !healthError &&
    (healthResult?.version.state === "incompatible" ||
      healthResult?.version.state === "update_available");
  const healthWarningBanner = isKintoneDependentActionBlocked ? null : (
    <HealthWarningBanner
      result={healthResult}
      error={healthError}
      isLoading={isHealthChecking}
      onRecheck={() => void runHealthCheck()}
      onRerunEasySetup={
        isEasySetupFeatureEnabled ? () => setRepairEasySetup(true) : undefined
      }
      onEditManually={() =>
        void handleStartEditing("storage", {
          openApiTokenEditor:
            healthError?.code === "kintone_api_token_missing" ||
            (healthResult?.connectivity.state === "failed" &&
              (healthResult.connectivity.kind === "unauthorized" ||
                healthResult.connectivity.kind === "forbidden")),
        })
      }
      pluginPackageHref={pluginPackageHref}
      systemPluginUrl={systemPluginUrl}
      appPluginSettingsUrl={pluginSettingsUrl}
      pluginPresenceDrift={pluginPresenceDrift}
      suppressPluginNotPresent={pluginNotAdded || pluginPublicationStarted}
      suppressUpdateAvailable={
        (pluginActionRequired && !combineVersionAndReflection) || pluginPresenceDrift
      }
      reflectionPending={combineVersionAndReflection}
      offerEasySetupForVersionRecovery={isEasySetupFeatureEnabled}
      easySetupHasVerifiedCredential={hasVerifiedSetupCredential}
    />
  );
  const pluginActionBanner =
    !isKintoneDependentActionBlocked &&
    pluginActionRequired &&
    !combineVersionAndReflection &&
    !pluginPresenceDrift ? (
      <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-4">
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
        <div className="flex-1 space-y-3">
          <div className="font-medium text-amber-900">{pluginActionTitle}</div>
          {pluginNotAdded ? (
            <>
              <p className="text-sm text-amber-800">
                対象アプリにはまだ EXTONE Drive プラグインが追加されていません。kintone
                アプリの未公開変更も一緒に公開される可能性があるため、確認してから追加とデプロイを実行してください。
              </p>
              <Button
                type="button"
                size="sm"
                disabled={isEnsuringPluginInstallation}
                onClick={() => setPluginPublishConfirmationOpen(true)}
              >
                {isEnsuringPluginInstallation
                  ? "プラグインを公開中..."
                  : "kintoneアプリへプラグインを公開（追加＋デプロイ）"}
              </Button>
            </>
          ) : pluginInstallationNeedsAction ? (
            <>
              <p className="text-sm text-amber-800">
                {pluginInstallationFailureReason === "confirm_required"
                  ? "プラグインを公開すると、このkintoneアプリに未公開の変更がある場合、それらも同時に公開されます。"
                  : pluginInstallationFailureReason === "permission_required"
                    ? "保存済み API トークンに「アプリ管理権限」がありません。"
                    : pluginInstallationFailureReason === "plugin_not_registered"
                      ? "プラグインファイルが kintone システムに未登録です。"
                      : "プラグイン追加状態を自動確認できませんでした。"}
              </p>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  size="sm"
                  disabled={isEnsuringPluginInstallation}
                  onClick={() => void handleEnsurePluginInstallation()}
                >
                  {isEnsuringPluginInstallation
                    ? "プラグイン追加状態を確認中..."
                    : "プラグイン追加状態を再確認"}
                </Button>
                {pluginManualSetupButton}
              </div>
              {pluginManualSetupOpen ? (
                <div className="rounded-md border border-amber-200 bg-background/80 p-3">
                  <div className="mb-2 text-sm font-medium text-amber-950">
                    手動セットアップに必要な操作
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <a
                      href={pluginPackageHref}
                      className={pluginLinkClass}
                    >
                      プラグイン(zip)をダウンロード
                      <Download className="h-3.5 w-3.5" />
                    </a>
                    {systemPluginUrl ? (
                      <a
                        href={systemPluginUrl}
                        target="_blank"
                        rel="noreferrer"
                        className={pluginLinkClass}
                      >
                        プラグインの登録画面を開く
                        <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    ) : null}
                  </div>
                  <p className="mt-2 text-sm text-amber-900">
                    プラグイン登録または権限修正が完了したら、上の確認ボタンで自動追加を再実行します。
                  </p>
                </div>
              ) : null}
            </>
          ) : pluginInstallationChecking ? (
            <>
              <p className="text-sm text-amber-800">
                保存済み API トークンで対象アプリのプラグイン追加状態を確認し、未追加なら自動追加を試みています。
              </p>
              <Button
                type="button"
                size="sm"
                disabled={isEnsuringPluginInstallation}
                onClick={() => void handleEnsurePluginInstallation()}
              >
                {isEnsuringPluginInstallation
                  ? "プラグイン追加状態を確認中..."
                  : "プラグイン追加状態を確認（必要なら追加）"}
              </Button>
            </>
          ) : needsActivation ? (
            <>
              <p className="text-sm text-amber-800">
                {pluginInstallState === "added_and_deployed"
                  ? "プラグインを対象アプリに追加し、kintone への反映を開始しました（反映完了まで数十秒かかる場合があります）。"
                  : "プラグインは対象アプリに追加済みです。"}
                {isEasySetupFeatureEnabled
                  ? "EXTONE Drive との初回連携が完了していません。簡単セットアップで自動連携するか、手動セットアップを開いて連携してください。"
                  : "EXTONE Drive との初回連携が完了していません。手動セットアップを開いて連携してください。"}
              </p>
              <div className="flex flex-wrap gap-2">
                {isEasySetupFeatureEnabled ? (
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => setRepairEasySetup(true)}
                  >
                    簡単セットアップを再実行
                  </Button>
                ) : null}
                {pluginManualSetupButton}
              </div>
              {pluginManualSetupOpen ? (
                <div className="rounded-md border border-amber-200 bg-background/80 p-3">
                  <div className="mb-2 text-sm font-medium text-amber-950">
                    手動セットアップに必要な操作
                  </div>
                  <p className="mb-3 text-sm text-amber-900">
                    プラグイン連携トークンを発行し、kintone プラグイン設定画面で貼り付けて初回連携します。
                  </p>
                  <Button
                    type="button"
                    size="sm"
                    disabled={isReflecting}
                    onClick={() => void handleRequestReflection()}
                  >
                    プラグイン連携トークンを発行
                  </Button>
                  {lastReflection ? (
                    <div className="mt-3 rounded-md bg-background/80 p-3">
                      <div
                        className="break-all font-mono text-xs"
                        data-kdrive-reflection-token
                      >
                        {lastReflection.handoffToken}
                      </div>
                      <div className="mt-2 text-xs text-amber-800">
                        期限: {new Date(lastReflection.handoffExpiresAt).toLocaleString("ja-JP")}
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => void handleCopyReflectionToken()}
                        >
                          <Clipboard className="mr-2 h-4 w-4" />
                          コピー
                        </Button>
                        {pluginSettingsUrl ? (
                          <a
                            href={pluginSettingsUrl}
                            target="_blank"
                            rel="noreferrer"
                            className={pluginLinkClass}
                          >
                            kintone プラグイン設定画面を開く
                            <ExternalLink className="h-3.5 w-3.5" />
                          </a>
                        ) : null}
                      </div>
                      <div className="mt-3 text-xs text-amber-800">
                        kintone プラグイン設定画面の「プラグイン連携トークン」に貼り付けて初回連携します。
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </>
          ) : (
            <>
              <p className="text-sm text-amber-800">
                保存済みの変更を kintone 側へ反映してください。反映するまでは従来の設定のまま動作します。
              </p>
              {pluginSettingsUrl || isEasySetupFeatureEnabled ? (
                <div className="space-y-3">
                  <div className="flex flex-wrap gap-2">
                    {isEasySetupFeatureEnabled ? (
                      <Button
                        type="button"
                        size="sm"
                        onClick={() => setRepairEasySetup(true)}
                      >
                        簡単セットアップを再実行
                      </Button>
                    ) : null}
                    {pluginSettingsUrl ? pluginManualSetupButton : null}
                  </div>
                  {pluginManualSetupOpen && pluginSettingsUrl ? (
                    <div className="rounded-md border border-amber-200 bg-background/80 p-3">
                      <a
                        href={pluginSettingsUrl}
                        target="_blank"
                        rel="noreferrer"
                        className={pluginLinkClass}
                      >
                        kintone プラグイン設定画面を開く
                        <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
    ) : null;
  const normalizedFieldBindings = useMemo(
    () => normalizeFieldBindings(fieldBindings),
    [fieldBindings],
  );
  const normalizedThumbnailSourceMaxBytes = useMemo(
    () => parseThumbnailSourceLimit(
      thumbnailSourceMaxBytes,
      DEFAULT_THUMBNAIL_SOURCE_MAX_BYTES,
    ),
    [thumbnailSourceMaxBytes],
  );
  const normalizedThumbnailSourceMaxPixels = useMemo(
    () => parseThumbnailSourceLimit(
      thumbnailSourceMaxPixels,
      DEFAULT_THUMBNAIL_SOURCE_MAX_PIXELS,
    ),
    [thumbnailSourceMaxPixels],
  );
  const thumbnailSourceMaxBytesInvalid = normalizedThumbnailSourceMaxBytes === null;
  const thumbnailSourceMaxPixelsInvalid = normalizedThumbnailSourceMaxPixels === null;
  const effectiveReplaceNativeAttachmentFields =
    modeId === "shared_storage" ? true : replaceNativeAttachmentFields;
  const canSave =
    (!hydratedBinding || automaticAlignment.canSaveSettings) &&
    Boolean(effectiveKintoneAppId) &&
    Boolean(effectiveKintoneSubdomain) &&
    Boolean(effectiveKintoneAppName.trim()) &&
    Boolean(storageId) &&
    !isKintoneDependentActionBlocked &&
    Boolean(normalizeRequiredAppBaseFolder(appBaseFolder)) &&
    normalizedThumbnailSourceMaxBytes !== null &&
    normalizedThumbnailSourceMaxPixels !== null &&
    (!editTokenOpen || Boolean(kintoneApiToken.trim())) &&
    (modeId !== "attachment_compatible" ||
      recordFolderKind === "record_id");
  const currentStep: AppBindingStepKey = !effectiveKintoneAppId ? "target" : wizardStep;
  const newAppBindingTitle = t("edit.newPageTitle");
  const pageTitle =
    effectiveKintoneAppName.trim() ||
    (isEditing ? t("edit.title") : newAppBindingTitle);

  // Load existing binding into form
  useEffect(() => {
    if (!hydratedBinding) return;
    const isFirstHydrationForBinding =
      hydratedBindingIdRef.current !== hydratedBinding.appBindingId;
    hydratedBindingIdRef.current = hydratedBinding.appBindingId;
    if (!savedView && !isFirstHydrationForBinding) return;
    hydrateEditableStateFromBinding(hydratedBinding, {
      setStorageId,
      setModeId,
      setAppBaseFolder,
      setHeaderLauncherEnabled,
      setReplaceNativeAttachmentFields,
      setShowHiddenStorageEntries,
      setMaxUploadFileBytes,
      setThumbnailSourceMaxBytes,
      setThumbnailSourceMaxPixels,
      setSharedStorageAccessMode,
      setCompatibleAttachmentRemovalPolicy,
      setRecordFolderKind,
      setRecordFolderLabelFieldBindingKey,
      setFieldBindings,
    });
    if (
      isEditing &&
      isFirstHydrationForBinding &&
      !initialSetupFieldContinuationActiveRef.current
    ) {
      setSavedView(true);
      setWizardStep("save");
    }
  }, [hydratedBinding, isEditing, savedView]);

  useEffect(() => {
    if (!alignmentAvailable) {
      setAlignmentSectionOpen(false);
      setRestoreSectionOpen(false);
    }
  }, [alignmentAvailable]);

  // Reset record folder kind when mode changes
  useEffect(() => {
    if (modeId !== "attachment_compatible") {
      setRecordFolderLabelFieldBindingKey("");
    }
  }, [modeId]);

  // Pre-fill field bindings from probe result
  useEffect(() => {
    if (!probeResult || isEditing) return;
    if (probeResult.probeItems.length > 0) {
      setFieldBindings((current) =>
        mergeImportedCandidates(current, probeResult.probeItems, {
          selectNewCandidates: false,
        }),
      );
    }
  }, [probeResult, isEditing]);

  function updateFieldBinding(
    fieldBindingKey: string,
    updater: (current: EditableFieldBinding) => EditableFieldBinding,
  ) {
    setFieldBindings((current) =>
      current.map((b) => (b.fieldBindingKey === fieldBindingKey ? updater(b) : b)),
    );
  }

  function updateAllFieldSelections(selected: boolean) {
    setFieldBindings((current) =>
      current.map((binding) => ({
        ...binding,
        selected,
      })),
    );
  }

  function canSelectWizardStep(step: AppBindingStepKey) {
    if (step === "target") return !isEditing;
    if (!effectiveKintoneAppId) return false;
    if (step === "storage") return true;
    if (step === "fields") return Boolean(storageId);
    if (step === "save") return Boolean(storageId);
    return false;
  }

  function resetEditableStateFromBinding() {
    if (!hydratedBinding) return;
    hydrateEditableStateFromBinding(hydratedBinding, {
      setStorageId,
      setModeId,
      setAppBaseFolder,
      setHeaderLauncherEnabled,
      setReplaceNativeAttachmentFields,
      setShowHiddenStorageEntries,
      setMaxUploadFileBytes,
      setThumbnailSourceMaxBytes,
      setThumbnailSourceMaxPixels,
      setSharedStorageAccessMode,
      setCompatibleAttachmentRemovalPolicy,
      setRecordFolderKind,
      setRecordFolderLabelFieldBindingKey,
      setFieldBindings,
    });
  }

  function returnToSavedDetailView() {
    initialSetupFieldContinuationActiveRef.current = false;
    setSubmitError(null);
    setStatusMessage(null);
    setEditTokenOpen(false);
    setKintoneApiToken("");
    resetEditableStateFromBinding();
    setSavedView(true);
    setWizardStep("save");
  }

  function returnToAppAddition() {
    setSubmitError(null);
    setStatusMessage(null);
    setPhase(1);
    setWizardStep("target");
  }

  function handleBackFromSharedSettings() {
    if (isEditing) {
      returnToSavedDetailView();
      return;
    }
    returnToAppAddition();
  }

  function handlePageBack() {
    if (!savedView && effectiveKintoneAppId) {
      handleBackFromSharedSettings();
      return;
    }
    navigate("/console/app-bindings");
  }

  async function handleImportFieldSettings() {
    if (!effectiveKintoneAppId || isKintoneDependentActionBlocked) return;
    try {
      const targets = normalizedFieldBindings.map((b) => ({
        fieldBindingKey: b.fieldBindingKey,
        fieldCode: b.fieldCode,
        targetScopeKind: b.targetScopeKind,
        subtableFieldCode: b.targetScopeKind === "subtable_row" ? b.subtableFieldCode : null,
      }));
      const result = await importFieldSettings(targets.length ? targets : undefined);
      applyImportedFieldSettings(result, {
        overwriteExistingThumbnailSettings: true,
      });
      setStatusMessage("kintone 設定を取り込みました。");
    } catch {
      // hook exposes error
    }
  }

  async function handleStartEditing(
    initialStep: AppBindingStepKey = "storage",
    options: { openApiTokenEditor?: boolean } = {},
  ) {
    if (isKintoneDependentActionBlocked) {
      return;
    }
    setSavedView(false);
    setWizardStep(initialStep);
    if (options.openApiTokenEditor) {
      setEditTokenOpen(true);
    }
    if (!isEditing || !effectiveKintoneAppId || !hydratedBinding?.kintoneApiTokenConfigured) {
      return;
    }
    try {
      await importSavedFieldSettingsForEditing(hydratedBinding);
    } catch {
      // hook exposes error; editing still opens with saved settings
    }
  }

  useEffect(() => {
    if (
      continueInitialSetupAt !== "fields" ||
      !hydratedBinding ||
      initialSetupFieldContinuationActiveRef.current ||
      isKintoneDependentActionBlocked
    ) {
      return;
    }

    initialSetupFieldContinuationActiveRef.current = true;
    setSubmitError(null);
    setStatusMessage(null);
    setSavedView(false);
    setWizardStep("fields");

    if (hydratedBinding.kintoneApiTokenConfigured) {
      void importSavedFieldSettingsForEditing(hydratedBinding).catch(() => {
        // The hook exposes a retryable error while the field editor stays open.
      });
    } else {
      setSubmitError(
        isEasySetupFeatureEnabled
          ? "保存済みの kintone API トークンを確認できません。簡単セットアップを再実行するか、トークンを設定してから再取得してください。"
          : "保存済みの kintone API トークンを確認できません。トークンを設定してから再取得してください。",
      );
    }

    navigate(`${location.pathname}${location.search}`, {
      replace: true,
      state: null,
    });
  }, [
    continueInitialSetupAt,
    isEasySetupFeatureEnabled,
    hydratedBinding,
    importSavedFieldSettingsForEditing,
    isKintoneDependentActionBlocked,
    location.pathname,
    location.search,
    navigate,
  ]);

  const [automaticAlignmentStartOnSetup, setAutomaticAlignmentStartOnSetup] = useState(true);

  const [settingsSaveConfirmation, setSettingsSaveConfirmation] = useState<{
    key: string; fields: string[]; automaticAlignmentEnabled: boolean;
    pending?: { executionId: string; revision: string };
  } | null>(null);

  async function handleSubmit(acknowledged?: NonNullable<typeof settingsSaveConfirmation>) {
    if (!canSave || !effectiveKintoneSubdomain || !effectiveKintoneAppId) return;
    setSubmitError(null);
    setStatusMessage(null);
    const uploadLimit = maxUploadFileBytes === "" ? null : Number(maxUploadFileBytes) * 1024 ** 2;
    if (uploadLimit !== null && (!/^\d+$/.test(maxUploadFileBytes) || !Number.isSafeInteger(uploadLimit)
        || uploadLimit < 1024 ** 2 || uploadLimit > 10737418240)) {
      setSubmitError("アップロード上限は1〜10,240MiBの整数で入力してください。未設定時はストレージ設定を使用します。");
      return;
    }
    if (!selectedStorage || !Number.isSafeInteger(selectedStorage.maxUploadFileBytes)) {
      setSubmitError("ストレージの上限を取得できません。画面を再読込してください。");
      return;
    }
    if (uploadLimit !== null && uploadLimit > selectedStorage.maxUploadFileBytes) {
      setSubmitError(`アップロード上限はストレージ上限（${selectedStorage.maxUploadFileBytes / 1024 ** 2}MiB）以下にしてください。`);
      return;
    }
    const apiToken = isEditing
      ? editTokenOpen
        ? kintoneApiToken || null
        : null
      : (probeResult?.kintoneApiToken ?? null);
    const guestSpaceId = isEditing ? null : (probeResult?.guestSpaceId ?? null);
    const submittedAppBaseFolder = normalizeRequiredAppBaseFolder(appBaseFolder);
    if (!submittedAppBaseFolder) {
      setSubmitError("アプリベースフォルダを入力してください。");
      return;
    }
    if (
      normalizedThumbnailSourceMaxBytes === null ||
      normalizedThumbnailSourceMaxPixels === null
    ) {
      setSubmitError("サムネイル化対象上限は公式上限以下の正の整数で入力してください。");
      return;
    }

    const active = automaticAlignment.state?.activeExecution;
    const pending = active?.executionKind === "attachment_storage_alignment" &&
      !automaticAlignment.settingsSaveBlockers.some(blocker => blocker.code === "management_execution_active")
        ? { executionId: active.executionId, revision: active.revision } : undefined;
    const externalFields = normalizedFieldBindings.filter(field => field.customAttachmentEnabled);
    const automaticAlignmentEnabled = hydratedBinding
      ? automaticAlignment.state?.state === "enabled"
      : automaticAlignmentStartOnSetup && externalFields.length > 0;
    const confirmation = {
      key: JSON.stringify({ storageId, modeId, submittedAppBaseFolder, normalizedFieldBindings,
        compatibleAttachmentRemovalPolicy, sharedStorageAccessMode, recordFolderKind,
        recordFolderLabelFieldBindingKey, headerLauncherEnabled, automaticAlignmentStartOnSetup,
        effectiveReplaceNativeAttachmentFields, showHiddenStorageEntries, uploadLimit,
        normalizedThumbnailSourceMaxBytes, normalizedThumbnailSourceMaxPixels, automaticAlignmentEnabled, pending }),
      fields: externalFields.map(field => field.fieldLabel || field.fieldCode),
      automaticAlignmentEnabled,
      pending,
    };
    if (acknowledged?.key !== confirmation.key) {
      setSettingsSaveConfirmation(confirmation);
      return;
    }
    setSettingsSaveConfirmation(null);
    try {
      const savedBinding = await upsertBinding({
        kintoneAppId: effectiveKintoneAppId,
        guestSpaceId,
        storageId,
        modeId,
        compatibleAttachmentRemovalPolicy: modeId === "attachment_compatible" ? compatibleAttachmentRemovalPolicy : "archive",
        sharedStorageAccessMode:
          modeId === "shared_storage" ? sharedStorageAccessMode : null,
        appBaseFolder: submittedAppBaseFolder,
        headerLauncherEnabled,
        ...(!isEditing ? { automaticAlignmentStartOnSetup } : {}),
        replaceNativeAttachmentFields: effectiveReplaceNativeAttachmentFields,
        showHiddenStorageEntries,
        maxUploadFileBytes: uploadLimit,
        thumbnailSourceProfile: {
          maxSourceBytes: normalizedThumbnailSourceMaxBytes,
          maxSourcePixels: normalizedThumbnailSourceMaxPixels,
        },
        recordFolderKind:
          modeId === "attachment_compatible" ? recordFolderKind : null,
        recordFolderLabelFieldBindingKey:
          modeId === "attachment_compatible" && recordFolderKind === "record_label_field"
            ? recordFolderLabelFieldBindingKey
            : null,
        kintoneApiToken: apiToken,
        targetFieldBindings: normalizedFieldBindings,
        externalizationConfirmed: externalFields.length > 0,
        ...(pending ? { pendingAlignmentCancellation: pending } : {}),
      });
      setEditTokenOpen(false);
      setKintoneApiToken("");
      if (!isEditing) {
        setProbeResult((current) =>
          current ? { ...current, kintoneApiToken: "" } : current,
        );
      }
      setStatusMessage({ kind: "saved" });
      initialSetupFieldContinuationActiveRef.current = false;
      setSavedView(true);
      setSavedDetailTab("settings");
      setAlignmentSectionOpen(false);
      setRestoreSectionOpen(false);
      // Saving persists service-side settings only. A saved binding is checked
      // once afterward, and app deploy is skipped unless it can run without
      // releasing unrelated kintone changes (DES-SETUP-007).
      setPluginInstallState(null);
      if (!isEditing) {
        navigate(
          `/console/app-bindings/${encodeURIComponent(savedBinding?.kintoneAppId ?? effectiveKintoneAppId)}`,
          {
            replace: true,
            state: {
              justSaved: true,
              savedBinding,
            },
          },
        );
        return;
      }
      if (routeKintoneAppId) {
        await refresh();
      }
    } catch (err) {
      void automaticAlignment.refresh();
      setSavedView(false);
      setWizardStep("save");
      const message =
        err instanceof Error
          ? err.message
          : "保存できませんでした。入力内容を確認してもう一度実行してください。";
      setSubmitError(message);
    }
  }

  async function handleEnsurePluginInstallation(
    confirm = false,
    options: { automatic?: boolean } = {},
  ): Promise<boolean> {
    if (!effectiveKintoneAppId || isKintoneDependentActionBlocked) return false;
    setSubmitError(null);
    try {
      const result = await ensurePluginInstallation(confirm);
      setPluginInstallState(result.state);
      if (result.state === "confirm_required") {
        if (options.automatic) {
          setStatusMessage(
            "kintoneアプリには未公開の変更があります。内容を確認してからプラグイン追加を再実行してください。",
          );
          await refresh();
          return false;
        }
        setPluginPublishConfirmationOpen(true);
        await refresh();
        return false;
      }
      if (result.state === "added_and_deployed") {
        setStatusMessage(
          "kintoneアプリへプラグインを追加し、デプロイを開始しました（反映完了まで数十秒かかる場合があります）。続けて初回連携方法を選択してください。",
        );
      } else if (result.state === "already_present") {
        setStatusMessage("プラグインは既にアプリに追加済みです。");
      } else if (result.state === "permission_required") {
        setSubmitError(
          "プラグイン追加に必要な kintoneアプリ管理権限がありません。権限付き API トークンに変更するか、kintone 管理者が手動で追加してください。",
        );
      } else if (result.state === "plugin_not_registered") {
        setSubmitError(
          "プラグインファイルが kintone システムに未登録です。先にプラグインを登録（アップロード）してから再確認してください。",
        );
      } else if (result.state === "unavailable") {
        setSubmitError(
          "プラグイン追加状態を確認できませんでした。kintone への接続、保存済み API トークン、またはサービス側のプラグイン ID 設定を確認してください。",
        );
      }
      await refresh();
      return true;
    } catch (err) {
      setSubmitError(
        err instanceof Error
          ? err.message
          : "プラグイン追加状態を確認できませんでした。時間をおいて再度実行してください。",
      );
      return false;
    }
  }

  async function handleConfirmPluginPublication() {
    const succeeded = await handleEnsurePluginInstallation(true);
    if (succeeded) {
      setPluginPublishConfirmationOpen(false);
    }
  }

  useEffect(() => {
    if (
      !savedView ||
      !hydratedBinding ||
      !hydratedBinding.kintoneApiTokenConfigured ||
      hydratedBinding.pluginSetupStatus !== "plugin_installation_unverified" ||
      isEnsuringPluginInstallation ||
      isKintoneDependentActionBlocked
    ) {
      return;
    }
    const attemptKey = `${hydratedBinding.appBindingId}:${hydratedBinding.updatedAt ?? ""}`;
    if (autoPluginInstallAttemptedRef.current.has(attemptKey)) {
      return;
    }
    autoPluginInstallAttemptedRef.current.add(attemptKey);
    void handleEnsurePluginInstallation(false, { automatic: true });
  }, [
    savedView,
    hydratedBinding?.appBindingId,
    hydratedBinding?.updatedAt,
    hydratedBinding?.kintoneApiTokenConfigured,
    hydratedBinding?.pluginSetupStatus,
    isEnsuringPluginInstallation,
    isKintoneDependentActionBlocked,
  ]);

  // Detail-open read-only health check (DES-SETUP-020): once per binding revision.
  useEffect(() => {
    if (
      !savedView ||
      !hydratedBinding ||
      isKintoneDependentActionBlocked
    ) return;
    const key = `${hydratedBinding.appBindingId}:${hydratedBinding.updatedAt ?? ""}`;
    if (healthCheckRanRef.current === key) return;
    healthCheckRanRef.current = key;
    void runHealthCheck();
  }, [
    savedView,
    hydratedBinding?.appBindingId,
    hydratedBinding?.updatedAt,
    isKintoneDependentActionBlocked,
    runHealthCheck,
  ]);

  async function handleRequestReflection() {
    if (!effectiveKintoneAppId) return;
    try {
      const result = await requestReflection();
      setLastReflection({
        handoffToken: result.handoffToken,
        handoffExpiresAt: result.handoffExpiresAt,
      });
      setStatusMessage("プラグイン連携トークンを発行しました。");
      await refresh();
    } catch {
      // hook exposes error
    }
  }

  async function handleDisconnectPluginLinkage() {
    if (!effectiveKintoneAppId || !pluginLinkageActivated) return;
    try {
      await disconnectPluginLinkage();
      setPluginLinkageDisconnectConfirmationOpen(false);
      setLastReflection(null);
      setStatusMessage("プラグイン連携を解除しました。準備中に戻りました。");
      await refresh();
    } catch {
      // hook exposes error
    }
  }

  async function handleConfirmDeleteAppBinding() {
    if (
      !effectiveKintoneSubdomain ||
      !effectiveKintoneAppId ||
      !deleteAppBindingConfirmation
    ) {
      return;
    }
    try {
      await deleteAppBinding({
        force: deleteAppBindingConfirmation.force ? true : undefined,
      });
      setDeleteAppBindingConfirmation(null);
      navigate("/console/app-bindings", {
        replace: true,
        state: {
          statusMessage:
            "kintoneアプリの設定を削除しました。ストレージを削除する場合は、使用中のアプリ設定が残っていないことを確認してください。",
        },
      });
    } catch {
      // hook exposes error
    }
  }

  async function handleCopyReflectionToken() {
    if (!lastReflection) return;
    try {
      await navigator.clipboard.writeText(lastReflection.handoffToken);
      setStatusMessage("プラグイン連携トークンをコピーしました。");
    } catch {
      setStatusMessage("コピーできませんでした。トークン文字列を選択してコピーしてください。");
    }
  }

  async function registerInitialEnvironmentOnce(
    ownership:
      | {
          verificationMethod: "new_api_token";
          kintoneAppUrl: string;
          apiToken: string;
        }
      | {
          verificationMethod: "administrator_credentials";
          kintoneAppId: string;
          administratorLoginName: string;
          administratorPassword: string;
          administratorMfa?: { kind: "disabled" } | { kind: "totp_key"; setupKey: string } | { kind: "totp_runtime" };
          inputConnection?: SetupInputConnection;
        },
  ) {
    if (currentKintoneSubdomain || initialEnvironmentRegistered) return;
    if (!candidateKintoneSubdomain ||
        !currentEnvironment ||
        currentEnvironment.kintoneConnectionState !== "unregistered") {
      throw new Error("初回kintone環境の状態を確認できません。画面を再読み込みしてください。");
    }
    const connectionAccess = initialSetupSession.connectionAccessFor(
      currentCustomerId,
      candidateKintoneSubdomain,
    );
    if (!connectionAccess) {
      throw new Error("初回kintone環境の接続情報が失われました。kintoneアプリ画面からやり直してください。");
    }
    const fingerprint = [
      candidateKintoneSubdomain,
      ownership.verificationMethod,
      ownership.verificationMethod === "new_api_token"
        ? ownership.kintoneAppUrl
        : ownership.kintoneAppId,
      currentEnvironment.environmentGeneration,
      initialSetupSession.session?.revision ?? 0,
      connectionAccess.kind,
    ].join("|");
    let command = initialRegistrationCommandRef.current;
    if (!command || command.fingerprint !== fingerprint) {
      const { inputConnection, ...verificationInput } = ownership.verificationMethod === "administrator_credentials" ? ownership : { ...ownership, inputConnection: undefined };
      const verificationBody = {
        ...verificationInput,
        targetSubdomain: candidateKintoneSubdomain,
        connectionAccess,
      };
      const verification = inputConnection ? await inputConnection.request<Awaited<ReturnType<typeof verifyKintoneEnvironmentOwnership>>>("verify_environment", verificationBody) : await verifyKintoneEnvironmentOwnership(verificationBody);
      if ("state" in verification) {
        throw new Error(
          "初回kintone環境の所有確認応答を確認できませんでした。画面を再読み込みしてやり直してください。",
        );
      }
      const challenge = verification;
      if (challenge.connectionAccessKind !== connectionAccess.kind) {
        throw new Error("初回kintone環境の接続方法が確認要求と一致しません。");
      }
      command = {
        fingerprint,
        idempotencyKey: crypto.randomUUID(),
        body: {
          challengeId: challenge.challengeId,
          targetSubdomain: challenge.targetSubdomain,
          expectedEnvironmentGeneration: currentEnvironment.environmentGeneration,
          connectionAccess,
        },
      };
      initialRegistrationCommandRef.current = command;
    }
    // The API invalidates display reads before its promise resolves. Keep the
    // setup flow mounted during that notification as well as the later refresh.
    setInitialEnvironmentRegistrationPending(true);
    try {
      try {
        await registerInitialKintoneEnvironment(command.body, command.idempotencyKey);
      } catch (cause) {
        try {
          const settled = await fetchCurrentKintoneEnvironment();
          if (settled.kintoneConnectionState === "registered" &&
              settled.kintoneSubdomain === candidateKintoneSubdomain &&
              settled.environmentGeneration === command.body.expectedEnvironmentGeneration) {
            setInitialEnvironmentRegistered(true);
            setCompletedInitialConnectionAccessKind(connectionAccess.kind);
            initialRegistrationCommandRef.current = null;
            initialSetupSession.clear();
            await refreshMe();
            await refreshCurrentEnvironment();
            return;
          }
        } catch {
          // 状態の再読込にも失敗した場合は、同一commandを次回そのまま再送する。
        }
        if (cause instanceof ApiError && cause.status < 500) {
          initialRegistrationCommandRef.current = null;
        }
        throw cause;
      }
      initialRegistrationCommandRef.current = null;
      setInitialEnvironmentRegistered(true);
      setCompletedInitialConnectionAccessKind(connectionAccess.kind);
      initialSetupSession.clear();
      await refreshMe();
      await refreshCurrentEnvironment();
    } finally {
      setInitialEnvironmentRegistrationPending(false);
    }
  }

  if (requestedCandidateSubdomain && !candidateKintoneSubdomain) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold">{newAppBindingTitle}</h1>
        <div className="mt-6 max-w-xl rounded-md border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive" role="alert">
          kintoneサブドメインが正しくありません。kintoneアプリ画面から入力し直してください。
        </div>
        <Button className="mt-4" variant="outline" onClick={() => navigate("/console/app-bindings")}>
          kintoneアプリへ戻る
        </Button>
      </div>
    );
  }

  if (!isEditing && !isCustomerAdmin && !currentKintoneSubdomain && candidateKintoneSubdomain) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold">{newAppBindingTitle}</h1>
        <div className="mt-6 max-w-xl rounded-md border border-amber-300 bg-amber-50 p-4 text-sm" role="alert">
          kintone環境の登録には管理者権限が必要です。組織管理者に登録を依頼してください。
        </div>
        <Button className="mt-4" variant="outline" onClick={() => navigate("/console/app-bindings", { replace: true })}>
          kintoneアプリへ戻る
        </Button>
      </div>
    );
  }

  if (!currentKintoneSubdomain && candidateKintoneSubdomain &&
      !initialEnvironmentRegistered && !initialConnectionAccess) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold">{newAppBindingTitle}</h1>
        <div className="mt-6 max-w-xl rounded-md border border-amber-300 bg-amber-50 p-4 text-sm" role="alert">
          初回kintone環境の接続情報がありません。再読み込みや組織切替後は、証明書を復元せず最初から入力します。
        </div>
        <Button className="mt-4" variant="outline" onClick={() => navigate("/console/app-bindings", { replace: true })}>
          kintone環境の登録へ戻る
        </Button>
      </div>
    );
  }

  if (currentKintoneSubdomain &&
      candidateKintoneSubdomain &&
      currentKintoneSubdomain !== candidateKintoneSubdomain) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold">{newAppBindingTitle}</h1>
        <div className="mt-6 max-w-xl rounded-md border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive" role="alert">
          登録済みのkintone環境と指定されたサブドメインが一致しません。現在の環境を確認してください。
        </div>
        <Button className="mt-4" variant="outline" onClick={() => navigate("/console/app-bindings")}>
          kintoneアプリへ戻る
        </Button>
      </div>
    );
  }

  // A registration response can invalidate reads before the caller receives it.
  // Keep EasySetupFlow mounted so its selected app and in-flight job survive.
  if (!initialEnvironmentRegistrationPending && !initialEnvironmentRegistered && !currentKintoneSubdomain && candidateKintoneSubdomain && currentEnvironmentLoading) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold">{newAppBindingTitle}</h1>
        <p className="mt-6 flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          kintone環境を確認しています
        </p>
      </div>
    );
  }

  if (!initialEnvironmentRegistrationPending && !initialEnvironmentRegistered && !currentKintoneSubdomain &&
      candidateKintoneSubdomain &&
      (!currentEnvironment || currentEnvironmentError)) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold">{newAppBindingTitle}</h1>
        <div className="mt-6 max-w-xl rounded-md border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive" role="alert">
          {currentEnvironmentError ?? "kintone環境を確認できません。再読み込みしてください。"}
        </div>
        <Button className="mt-4" variant="outline" onClick={() => void refreshCurrentEnvironment()}>
          再読み込み
        </Button>
      </div>
    );
  }

  function handleProbeSuccess(result: {
    guestSpaceId: string | null;
    kintoneApiToken: string;
    customerId: string;
    kintoneSubdomain: string;
    kintoneAppId: string;
    kintoneAppName: string;
    modeId: AppBindingModeId;
    probeItems: ProbeItem[];
    recordFolderLabelFieldCandidates: RecordFolderLabelFieldCandidate[];
  }) {
    // A successful new easy setup has already persisted the app-level settings
    // with zero target fields. Continue once at the saved binding's field editor
    // so the next action is explicit field selection rather than detail review.
    const wasRepair = repairEasySetup;
    if (!wasRepair && setupMethod === "easy" && isEasySetupFeatureEnabled) {
      navigate(
        `/console/app-bindings/${encodeURIComponent(result.kintoneAppId)}`,
        {
          replace: true,
          state: { continueInitialSetupAt: "fields" },
        },
      );
      return;
    }

    setProbeResult(result);
    setModeId(result.modeId);
    setAppBaseFolder(
      defaultAppBaseFolderForMode({
        modeId: result.modeId,
        kintoneAppId: result.kintoneAppId,
      }),
    );
    // Repair re-run also ends here; clear the repair branch so the phase-2
    // wizard renders (its return guard is not gated on `phase`). Without this a
    // successful repair would re-render EasySetupFlow and strand the user.
    setRepairEasySetup(false);
    setPhase(2);
    setWizardStep("storage");
    // A successful repair re-run fixed the kintone-side state (token re-issued +
    // redeployed). The detail-open health check only runs once per
    // appBindingId:updatedAt, so without forcing a refresh the stale
    // "API トークンが拒否されました" banner persists until a manual reload. Refresh
    // the binding (server-derived pluginSetupStatus / token configured) and re-run
    // the health check now so returning to detail shows the corrected state.
    if (wasRepair) {
      healthCheckRanRef.current = null;
      void refresh();
      if (!isKintoneDependentActionBlocked) {
        void runHealthCheck();
      }
    }
  }

  if (
    !isEditing &&
    phase === 1 &&
    setupMethod === "easy" &&
    isEasySetupFeatureEnabled
  ) {
    return (
      <EasySetupFlow
        candidateSubdomain={
          currentKintoneSubdomain ? undefined : candidateKintoneSubdomain
        }
        candidateConnectionAccess={initialConnectionAccess ?? undefined}
        onRegisterInitialEnvironment={
          currentKintoneSubdomain || initialEnvironmentRegistered
            ? undefined
            : (input) => registerInitialEnvironmentOnce({
                verificationMethod: "administrator_credentials",
                ...input,
              })
        }
        onProbeSuccess={handleProbeSuccess}
        onFallbackToManual={() => setSetupMethod("manual")}
        onBack={() => navigate("/console/app-bindings")}
      />
    );
  }

  // Repair re-run from the detail/health banner: prefilled, mode="repair".
  if (repairEasySetup && hydratedBinding && isEasySetupFeatureEnabled) {
    return (
      <EasySetupFlow
        mode="repair"
        prefill={{
          kintoneAppId: hydratedBinding.kintoneAppId,
          guestSpaceId:
            hydratedBinding.kintoneSpaceKind === "guest_space"
              ? hydratedBinding.kintoneSpaceId ?? null
              : null,
          modeId: hydratedBinding.modeId,
        }}
        onProbeSuccess={handleProbeSuccess}
        onFallbackToManual={() => setRepairEasySetup(false)}
        onBack={() => setRepairEasySetup(false)}
      />
    );
  }

  if (!isEditing && phase === 1) {
    return (
      <div className="p-6">
        <div className="mb-6 flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/console/app-bindings")}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-2xl font-bold">{newAppBindingTitle}</h1>
        </div>
        {isEasySetupFeatureEnabled ? (
          <Button variant="link" onClick={() => setSetupMethod("easy")}>簡単セットアップに戻る</Button>
        ) : <p className="mb-4 text-sm text-muted-foreground">{easySetupUnavailableReason}手動セットアップで設定してください。</p>}
        <div className="max-w-2xl">
          <NewBindingPhase1
            currentKintoneSubdomain={onboardingKintoneSubdomain}
            currentCustomerId={currentCustomerId}
            connectionAccessKind={connectionAccessKindForLinks}
            onRegisterInitialEnvironment={
              currentKintoneSubdomain || initialEnvironmentRegistered
                ? undefined
                : (input) => registerInitialEnvironmentOnce({
                    verificationMethod: "new_api_token",
                    kintoneAppUrl: input.kintoneAppUrl,
                    apiToken: input.apiToken,
                  })
            }
            onProbeSuccess={handleProbeSuccess}
          />
        </div>
      </div>
    );
  }

  if (savedView) {
    const externalAttachmentFields = hydratedBinding?.targetFieldBindings.filter(
      (field) => field.customAttachmentEnabled,
    ) ?? [];
    const hasExternalAttachmentFields = externalAttachmentFields.length > 0;
    const externalAttachmentFieldNames = externalAttachmentFields
      .slice(0, 3)
      .map((field) => field.fieldCode)
      .join("、");
    const savedModeLabel = getAppBindingModeLabel(hydratedBinding?.modeId);

    return (
      <div className="p-6">
        <div className="mb-6 flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            aria-label="kintoneアプリ一覧へ戻る"
            onClick={() => navigate("/console/app-bindings")}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <div className="text-sm text-muted-foreground">kintoneアプリ</div>
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-3xl font-bold tracking-normal">{pageTitle}</h1>
              {hydratedBinding ? (
                <AppBindingStatusBadges binding={hydratedBinding} t={t} />
              ) : null}
            </div>
          </div>
        </div>
        <TargetAppSummary
          subdomain={extractedSubdomain}
          appId={effectiveKintoneAppId}
          modeLabel={savedModeLabel}
          appUrl={currentKintoneAppUrl}
          pluginLinkageLabel={pluginLinkageLabel(statusSummaryBinding)}
          settingReflectionLabel={settingReflectionLabel(statusSummaryBinding)}
        />

        {configurationWarningBanner}
        {connectionAccessStatusBanner}
        {healthWarningBanner}
        {pluginActionBanner}

        {visibleStatusMessage && !pluginActionRequired ? (
          <div className="mb-4 rounded-md bg-primary/10 p-3 text-sm">{visibleStatusMessage}</div>
        ) : null}

        {submitError ||
        pluginInstallationError ||
        reflectionError ||
        disconnectPluginLinkageError ||
        deleteAppBindingError ||
        bindingError ||
        storageError ? (
          <div className="mb-4 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
            {submitError ||
              pluginInstallationError ||
              reflectionError ||
              disconnectPluginLinkageError ||
              deleteAppBindingError ||
              bindingError ||
              storageError}
          </div>
        ) : null}

        <div className="mb-4 flex items-center justify-between gap-3">
          <div className="text-sm text-muted-foreground">保存済みの設定内容</div>
          <Button
            type="button"
            variant="outline"
            disabled={isKintoneDependentActionBlocked}
            onClick={() => void handleStartEditing(savedDetailTabEditStep(savedDetailTab))}
          >
            設定を編集
          </Button>
        </div>

        <div
          aria-label="kintoneアプリタブ"
          className="mb-6 flex w-full gap-2 overflow-x-auto px-0.5 py-1"
        >
          {SAVED_DETAIL_TABS.map((tab) => {
            const isActive = savedDetailTab === tab.key;
            return (
              <button
                type="button"
                key={tab.key}
                onClick={() => setSavedDetailTab(tab.key)}
                className={[
                  "min-w-[160px] flex-1 select-none cursor-pointer rounded-lg border px-3 py-2 text-left text-sm shadow-sm transition-all duration-150 active:translate-y-0 active:shadow-sm",
                  isActive
                    ? "border-primary bg-primary/10 font-medium text-primary shadow"
                    : "border-border bg-background text-muted-foreground hover:-translate-y-0.5 hover:border-primary hover:shadow",
                ].join(" ")}
              >
                {t(tab.labelKey)}
              </button>
            );
          })}
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {t(savedDetailTabTitleKey(savedDetailTab))}
            </CardTitle>
          </CardHeader>
          {savedDetailTab === "settings" ? (
              <CardContent className="space-y-5 text-sm">
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <div className="text-xs text-muted-foreground">保存先ストレージ</div>
                    <div>
                      {savedStorage ? (
                        <Link
                          to={`/console/storages/${savedStorage.storageId}`}
                          className="font-medium text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                        >
                          {savedStorage.displayName}
                        </Link>
                      ) : (
                        "-"
                      )}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">
                      アプリベースフォルダ
                    </div>
                    <div className="font-mono">
                      {savedDetailAppBaseFolderMissing ? "-" : savedDetailAppBaseFolder}
                    </div>
                  </div>
                  {hydratedBinding?.modeId === "attachment_compatible" ? (
                    <div>
                      <div className="text-xs text-muted-foreground">レコードフォルダの命名方法</div>
                      <div>{getRecordFolderKindLabel(hydratedBinding.recordFolderKind ?? "record_id")}</div>
                    </div>
                  ) : null}
                  {hydratedBinding?.modeId === "shared_storage" ? (
                    <div>
                      <div className="text-xs text-muted-foreground">共有ストレージ権限</div>
                      <div>
                        {t(
                          `edit.sharedStorageAccessModes.${sharedStorageAccessModeKey(
                            hydratedBinding.sharedStorageAccessMode,
                          )}`,
                        )}
                      </div>
                    </div>
                  ) : null}
                </div>
                {savedDetailAppBaseFolderMissing ? (
                  <div role="alert" className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                    {isEasySetupFeatureEnabled
                      ? "アプリベースフォルダが保存されていません。設定正本が不整合です。設定を編集して保存し直すか、簡単セットアップを再実行してください。"
                      : "アプリベースフォルダが保存されていません。設定正本が不整合です。設定を編集して保存し直してください。"}
                  </div>
                ) : null}
                <details className="rounded-lg border border-border">
                  <summary className="cursor-pointer select-none px-4 py-3 text-sm font-medium">
                    詳細情報
                  </summary>
                  <div className="space-y-4 px-4 pb-4 pt-2">
                    <div className="grid gap-4 md:grid-cols-2">
                      <div>
                        <div className="text-xs text-muted-foreground">
                          {t("edit.pluginTransferPolicy.corsStateLabel")}
                        </div>
                        <div>
                          {t(
                            `edit.pluginTransferPolicy.corsStates.${pluginCorsStateKey(
                              hydratedBinding?.pluginTransferPolicy.corsState,
                            )}`,
                          )}
                        </div>
                      </div>
                      {hydratedBinding?.pluginTransferPolicy.reasonCode && (
                        <div>
                          <div className="text-xs text-muted-foreground">{t("edit.pluginTransferPolicy.reasonLabel")}</div>
                          <div>{t(`edit.pluginTransferPolicy.reasons.${hydratedBinding.pluginTransferPolicy.reasonCode}`)}</div>
                        </div>
                      )}
                      <div>
                        <div className="text-xs text-muted-foreground">
                          {t("edit.pluginTransferPolicy.uploadExecutorLabel")}
                        </div>
                        <div>
                          {t(
                            `edit.pluginTransferPolicy.uploadExecutors.${pluginUploadExecutorKey(
                              hydratedBinding?.pluginTransferPolicy.uploadExecutor,
                            )}`,
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="border-t border-border pt-4">
                      <div className="mb-3 font-medium">サムネイル生成</div>
                      <div className="grid gap-4 md:grid-cols-3">
                        <div>
                          <div className="text-xs text-muted-foreground">
                            サムネイル化対象最大サイズ
                          </div>
                          <div className="font-mono">
                            {formatInteger(
                              hydratedBinding?.thumbnailSourceProfile.maxSourceBytes,
                            )}{" "}
                            bytes
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground">
                            サムネイル化対象最大画素数
                          </div>
                          <div className="font-mono">
                            {formatInteger(
                              hydratedBinding?.thumbnailSourceProfile.maxSourcePixels,
                            )}{" "}
                            画素
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground">対象形式</div>
                          <div>
                            {formatThumbnailSourceFormats(
                              hydratedBinding?.thumbnailSourceProfile
                                ?.supportedImageFormats,
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </details>

              </CardContent>
          ) : (
              <CardContent className="space-y-3 text-sm">
                {normalizedFieldBindings.length === 0 ? (
                  <div className="rounded-md border border-dashed border-border p-4 text-sm text-muted-foreground">
                    対象フィールドは設定されていません。すべての FILE フィールドは kintone 標準添付のまま保存されます。
                  </div>
                ) : null}
                {normalizedFieldBindings.map((field) => (
                  <div
                    key={field.fieldBindingKey}
                    data-field-binding-key={field.fieldBindingKey}
                    className="flex items-start gap-3 rounded-md border border-border p-3"
                  >
                    <div
                      className="mt-0.5 flex h-14 w-14 shrink-0 flex-col items-center justify-center gap-1 rounded-md border border-[#3498db]/30 bg-[#3498db]/10 text-[11px] font-medium leading-none text-[#3498db]"
                      aria-hidden="true"
                    >
                      <RuntimeCloudIcon className="h-[18px] w-[18px]" />
                      <span>外部</span>
                    </div>
                    <div className="min-w-0">
                      <div className="truncate font-medium">{field.fieldLabel || field.fieldCode}</div>
                      <div className="mt-1 text-xs text-muted-foreground">
                        サムネイル: {field.thumbnailEnabled ? field.thumbnailSizePx || "表示" : "非表示"}
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
          )}
        </Card>


        {hydratedBinding && alignmentAvailable && hydratedBinding.modeId === "attachment_compatible" ? (
          <details className="mt-6 rounded-lg border border-border"
            open={orphanSectionOpen} onToggle={event => setOrphanSectionOpen(event.currentTarget.open)}>
            <summary className="cursor-pointer px-4 py-3 text-sm font-medium">孤立レコードフォルダ整理</summary>
            {orphanSectionOpen ? <div className="px-4 pb-4"><OrphanRecordFolderCleanupSection binding={hydratedBinding} automaticAlignment={manualAlignment} /></div> : null}
          </details>
        ) : null}

        {hydratedBinding ? <AutomaticAttachmentAlignmentPanel externalizationFields={hydratedBinding.targetFieldBindings.filter(field => field.customAttachmentEnabled).map(field => field.fieldCode)} storageDisplayName={storages.find(storage => storage.storageId === hydratedBinding.storageId)?.displayName} modeId={hydratedBinding.modeId} controller={automaticAlignment} onEditSettings={() => void handleStartEditing(savedDetailTabEditStep(savedDetailTab))}>
          {alignmentAvailable ? <>
            <Button aria-haspopup="dialog" aria-controls="attachment-storage-alignment" type="button" variant="outline" size="sm" onClick={openAttachmentStorageAlignment}>整合化</Button>
            <Button aria-haspopup="dialog" aria-controls="attachment-environment-restore" type="button" variant="outline" size="sm" onClick={() => { setExportSectionOpen(false); setAlignmentSectionOpen(false); setRestoreSectionOpen(true); }}>復元</Button>
          </> : null}
        </AutomaticAttachmentAlignmentPanel> : null}
        {hydratedBinding ? <div className="space-y-2 text-sm" aria-label="保守操作の実行状態" aria-live="polite">
          {[alignmentStatus, restoreStatus].map((status, index) => status ? <div key={index}>
            {status.pending ? <p>操作を受け付けています。結果が確認できるまで新たな実行は開始しないでください。</p> : null}
            {status.error ? <p role="alert">{status.error} モーダルまたは実行履歴から状態を確認してください。</p> : null}
            {status.executionId ? <Link className="underline" to={`/console/asynchronous-executions/${status.executionId}?kintoneAppId=${encodeURIComponent(hydratedBinding.kintoneAppId)}`}>受け付けた{index === 0 ? "整合化・診断" : "復元・書き出し"}の実行詳細</Link> : null}
          </div> : null)}
          <Link className="inline-block py-1 underline" to={`/console/asynchronous-executions?kintoneAppId=${encodeURIComponent(hydratedBinding.kintoneAppId)}`}>このアプリの実行履歴</Link>
        </div> : null}
        {hydratedBinding && alignmentAvailable ? <>
          <MaintenanceDialog id="attachment-storage-alignment" key={`alignment:${currentCustomerId}:${hydratedBinding.appBindingId}:${hydratedBinding.latestSettingsRevision}`} open={alignmentSectionOpen} onClose={() => setAlignmentSectionOpen(false)} title="添付ファイル整合化">
            <p className="text-sm">対象アプリ：{hydratedBinding.kintoneAppId}</p>
            <AttachmentStorageAlignmentSection embedded storageDisplayName={storages.find(storage => storage.storageId === hydratedBinding.storageId)?.displayName} binding={hydratedBinding} automaticAlignment={manualAlignment} onStatusChange={setAlignmentStatus}
              onShowAutomatic={() => { setAlignmentSectionOpen(false); requestAnimationFrame(() => document.querySelector<HTMLElement>('[role="switch"][aria-label="自動整合化"]')?.focus()); }} />
          </MaintenanceDialog>
          <MaintenanceDialog id="attachment-environment-restore" key={`restore:${currentCustomerId}:${hydratedBinding.appBindingId}:${hydratedBinding.latestSettingsRevision}`} open={restoreSectionOpen} onClose={() => setRestoreSectionOpen(false)} title="添付ファイルの復元">
            <p className="text-sm">対象アプリ：{hydratedBinding.kintoneAppId}</p>
            <AttachmentEnvironmentRestoreSection embedded binding={hydratedBinding} automaticAlignment={manualAlignment} selectedStorage={selectedStorage} sourceStorages={storages}
              onShowAutomatic={() => { setRestoreSectionOpen(false); requestAnimationFrame(() => document.querySelector<HTMLElement>('[role="switch"][aria-label="自動整合化"]')?.focus()); }}
              showExport={restoreSectionOpen && exportSectionOpen} onStatusChange={setRestoreStatus} />
          </MaintenanceDialog>
        </> : null}

        {/* Destructive / management actions: kept at the very bottom, compact and
            collapsed, so they are not triggered by mistake. */}
        <details className="mt-10 rounded-lg border border-border bg-muted/10">
          <summary className="cursor-pointer select-none px-4 py-3 text-sm font-medium text-muted-foreground">
            管理操作（連携解除・設定削除）
          </summary>
          <div className="space-y-4 px-4 pb-4 pt-1">
            {isCustomerAdmin && isEasySetupFeatureEnabled ? (
              <div className="rounded-md border border-border p-3">
                <div className="text-sm font-medium">セットアップ中断後の修復</div>
                <p className="mt-1 text-xs text-muted-foreground">
                  保存済み設定を使ってkintoneとの連携を修復します。未確定のセットアップがある場合は、先に一覧で状態を確認してください。
                </p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="mt-3"
                  disabled={isKintoneDependentActionBlocked}
                  onClick={() => setRepairEasySetup(true)}
                >
                  簡単セットアップで連携を修復
                </Button>
              </div>
            ) : null}
            {pluginLinkageActivated ? (
              <div className="rounded-md border border-border p-3">
                <div className="text-sm font-medium">プラグイン連携の解除</div>
                <p className="mt-1 text-xs text-muted-foreground">
                  現在の kintone プラグイン連携を解除すると、準備中に戻してトークンを再発行できます。
                </p>
                <Button
                  type="button"
                  className="mt-3"
                  variant="destructive"
                  size="sm"
                  onClick={() => setPluginLinkageDisconnectConfirmationOpen(true)}
                  disabled={isDisconnectingPluginLinkage}
                >
                  プラグイン連携を解除
                </Button>
              </div>
            ) : null}
            <div className="rounded-md border border-destructive/30 p-3">
              <div className="text-sm font-medium text-destructive">
                kintoneアプリの設定削除
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                コンソール上の kintoneアプリの設定を削除します。プラグイン連携解除とは別操作です。この操作ではストレージ上のファイルや kintoneアプリ本体は削除されません。
              </p>
              <p className="mt-2 text-sm">再設定して復元する場合は、再設定時の自動開始をOFFにして、復元が終わってから有効にしてください。</p>
              {hydratedBinding?.modeId === "shared_storage" ? (
                <div className="mt-3 space-y-2 text-xs text-muted-foreground">
                  <p>復元情報が必要な場合は、設定を削除する前に書き出しを完了し、詳細画面からZIPを手元へ保存してください。設定削除後は、この設定からの書き出しとダウンロードができなくなります。ダウンロード開始だけでは保存完了を確認できません。</p>
                  <Button type="button" variant="outline" size="sm"
                    disabled={!alignmentAvailable} aria-controls="attachment-environment-restore"
                    onClick={openAttachmentEnvironmentRestore}>復元情報の書き出しを開く</Button>
                </div>
              ) : null}
              {hasExternalAttachmentFields ? (
                <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950">
                  <div className="flex items-start gap-2 font-medium">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>外部添付設定のフィールドが残っています</span>
                  </div>
                  <p className="mt-2 text-xs leading-5">
                    {externalAttachmentFields.length} 件のフィールド
                    {externalAttachmentFieldNames ? `（${externalAttachmentFieldNames}${externalAttachmentFields.length > 3 ? " ほか" : ""}）` : ""}
                    が外部添付対象です。連携を終了する場合は、先に設定編集で対象フィールドを解除し、
                    「添付ファイル整合化」で標準ストレージへ移行してから削除することを推奨します。
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={isKintoneDependentActionBlocked}
                      onClick={() => void handleStartEditing("fields")}
                    >
                      フィールド設定を編集
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      aria-controls="attachment-storage-alignment"
                      onClick={openAttachmentStorageAlignment}
                    >
                      添付ファイル整合化を開く
                    </Button>
                  </div>
                </div>
              ) : null}
              <Button
                type="button"
                className="mt-3"
                variant="destructive"
                size="sm"
                onClick={() =>
                  setDeleteAppBindingConfirmation({ force: false })
                }
                disabled={
                  isDeletingAppBinding ||
                  hasExternalAttachmentFields ||
                  !effectiveKintoneSubdomain ||
                  !effectiveKintoneAppId
                }
              >
                <Trash2 className="mr-2 h-4 w-4" />
                kintoneアプリの設定を削除
              </Button>
              {hasExternalAttachmentFields ? (
                <Button
                  type="button"
                  className="mt-3 ml-2"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setDeleteAppBindingConfirmation({ force: true })
                  }
                  disabled={
                    isDeletingAppBinding ||
                    !effectiveKintoneSubdomain ||
                    !effectiveKintoneAppId
                  }
                >
                  解除せず強制削除
                </Button>
              ) : null}
              {deleteAppBindingError ? (
                <div className="mt-3 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                  {deleteAppBindingError}
                </div>
              ) : null}
            </div>
          </div>
        </details>
        <ConfirmDialog
          open={pluginPublishConfirmationOpen}
          onClose={() => setPluginPublishConfirmationOpen(false)}
          onConfirm={() => handleConfirmPluginPublication()}
          title="kintoneアプリへプラグインを公開"
          description="プラグインを追加して公開すると、このkintoneアプリに未公開の変更がある場合、それらも同時に公開されます。続行しますか？"
          confirmText="公開"
          isLoading={isEnsuringPluginInstallation}
        />
        <ConfirmDialog
          open={pluginLinkageDisconnectConfirmationOpen}
          onClose={() => setPluginLinkageDisconnectConfirmationOpen(false)}
          onConfirm={() => handleDisconnectPluginLinkage()}
          title="プラグイン連携を解除"
          description="このkintoneアプリのプラグイン連携を解除すると、プラグイン機能が停止して再連携が必要になります。保存済みのアプリ設定、フィールド設定、ストレージ上のファイルは削除されません。"
          confirmText="連携解除"
          isLoading={isDisconnectingPluginLinkage}
          variant="destructive"
        />
        <ConfirmDialog
          open={deleteAppBindingConfirmation !== null}
          onClose={() => setDeleteAppBindingConfirmation(null)}
          onConfirm={() => handleConfirmDeleteAppBinding()}
          title={
            deleteAppBindingConfirmation?.force
              ? "kintoneアプリの設定を強制削除"
              : "kintoneアプリの設定を削除"
          }
          description={
            deleteAppBindingConfirmation?.force
              ? "外部添付設定のフィールドが残ったまま、このkintoneアプリの設定を強制削除します。削除後は再設定または復元手順が必要になる場合があります。ストレージ上のファイルとkintoneアプリ本体は削除されません。"
              : "このkintoneアプリの設定を削除します。ストレージ上のファイルはこの操作では削除されません。"
          }
          confirmText={deleteAppBindingConfirmation?.force ? "強制削除" : "削除"}
          isLoading={isDeletingAppBinding}
          variant="destructive"
        />
      </div>
    );
  }

  // Shared settings tabs for new bindings after probe and for existing bindings.
  return (
    <div className="p-6">
      <div className="mb-6 flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={handlePageBack}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          {isEditing || effectiveKintoneAppName.trim() ? (
            <div className="text-sm text-muted-foreground">kintoneアプリ</div>
          ) : null}
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-3xl font-bold tracking-normal">
              {pageTitle}
            </h1>
            {hydratedBinding ? (
              <AppBindingStatusBadges binding={hydratedBinding} t={t} />
            ) : null}
          </div>
        </div>
      </div>
      {!savedView ? (
        <>
          <TargetAppSummary
            subdomain={extractedSubdomain}
            appId={effectiveKintoneAppId}
            modeLabel={getAppBindingModeLabel(modeId)}
            appUrl={currentKintoneAppUrl}
            pluginLinkageLabel={pluginLinkageLabel(statusSummaryBinding)}
            settingReflectionLabel={settingReflectionLabel(statusSummaryBinding)}
          />
          {isBindingLoading ? (
            <p role="status" className="mb-4 text-sm text-muted-foreground">読み込み中...</p>
          ) : null}
          {configurationWarningBanner}
          {connectionAccessStatusBanner}
          {healthWarningBanner}
          {pluginActionBanner}
          {isEditing ? (
            <div className="mb-4 flex items-center justify-between gap-3">
              <div className="text-sm text-muted-foreground">
                保存済みの設定内容
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={returnToSavedDetailView}
              >
                編集をキャンセル
              </Button>
            </div>
          ) : null}
          <StepNavigation
            currentStep={currentStep}
            showTargetStep={!isEditing}
            canSelectStep={canSelectWizardStep}
            onStepSelect={(step) => {
              if (step === "target" && !isEditing) {
                setPhase(1);
                setWizardStep("target");
                return;
              }
              setWizardStep(step);
            }}
          />
        </>
      ) : null}

      <div className="grid gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {wizardStep === "storage"
                ? "アプリ設定"
                : wizardStep === "fields"
                  ? "フィールド設定"
                  : "確認/保存"}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {wizardStep === "storage" ? (
              <>
                <div className="grid gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="storageId">{t("edit.storage")}</Label>
                    <select
                      id="storageId"
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      value={storageId}
                      onChange={(e) => setStorageId(e.target.value)}
                      disabled={isStorageLoading}
                    >
                      <option value="">
                        {isStorageLoading
                          ? t("edit.storageLoading")
                          : t("edit.storagePlaceholder")}
                      </option>
                      {readyStorages.map((s) => (
                        <option key={s.storageId} value={s.storageId}>
                          {`${s.displayName} / ${storageKindLabel(s.connectionKindId, s.providerFamilyDisplayLabel)}`}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {isEditing ? (
                  <div className="space-y-3">
                    <details
                      className="rounded-lg border border-border"
                      open={editTokenOpen || undefined}
                    >
                      <summary className="cursor-pointer select-none px-4 py-3 text-sm font-medium">
                        API トークン変更
                      </summary>
                      <div className="space-y-3 px-4 pb-4 pt-2">
                        <div className="rounded-md border border-border bg-muted/30 px-3 py-2 text-sm">
                          {binding?.kintoneApiTokenConfigured ? "設定済み" : "未設定"}
                        </div>
                        {!editTokenOpen ? (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setEditTokenOpen(true)}
                          >
                            {binding?.kintoneApiTokenConfigured ? "変更" : "設定"}
                          </Button>
                        ) : (
                          <div className="space-y-2">
                            {apiTokenAdminUrl ? (
                              <a
                                href={apiTokenAdminUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-1 text-sm font-medium text-primary underline-offset-2 hover:underline"
                              >
                                kintone で API トークンを発行 / 確認
                                <ExternalLink className="h-3.5 w-3.5" />
                              </a>
                            ) : null}
                            <Label htmlFor="tokenReplacementApiToken">
                              kintone API トークン
                            </Label>
                            <Input
                              id="tokenReplacementApiToken"
                              type="password"
                              autoFocus
                              value={kintoneApiToken}
                              onChange={(e) => setKintoneApiToken(e.target.value)}
                              placeholder="新しい API トークン"
                              autoComplete="off"
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setEditTokenOpen(false);
                                setKintoneApiToken("");
                              }}
                            >
                              変更をやめる
                            </Button>
                          </div>
                        )}
                      </div>
                    </details>
                  </div>
                ) : null}

                <div className="rounded-lg border border-border bg-muted/20 p-4">
                  <div className="space-y-2">
                    <Label htmlFor="appBaseFolder">{t("edit.appBaseFolder")}</Label>
                    <Input
                      id="appBaseFolder"
                      value={appBaseFolder}
                      readOnly
                    />
                    <AppBaseFolderPickerButton
                      storageId={storageId}
                      appBindingId={hydratedBinding?.appBindingId}
                      suggestedPath={appBaseFolder || "/"}
                      onSelect={setAppBaseFolder}
                    />
                  </div>
                </div>

                {modeId === "attachment_compatible" && (
                  <div className="space-y-2">
                    <Label htmlFor="compatibleAttachmentRemovalPolicy">添付解除後のファイル</Label>
                    <select aria-describedby="compatibleAttachmentRemovalPolicyDescription" id="compatibleAttachmentRemovalPolicy" className="w-full rounded-md border border-input bg-background px-3 py-2" value={compatibleAttachmentRemovalPolicy} onChange={event => setCompatibleAttachmentRemovalPolicy(event.target.value as "archive" | "delete")}>
                      <option value="archive">履歴用に退避する</option>
                      <option value="delete">物理削除する</option>
                    </select>
                    <p
                      id="compatibleAttachmentRemovalPolicyDescription"
                      aria-live="polite"
                      className={`text-sm ${compatibleAttachmentRemovalPolicy === "delete" ? "text-destructive" : "text-muted-foreground"}`}
                    >
                      {compatibleAttachmentRemovalPolicy === "delete"
                        ? "添付を解除したファイルをストレージから削除します。更新履歴から参照したり復元できなくなります。"
                        : "添付を解除したファイルを更新履歴用に保存します。（推奨）"}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      kintoneへの設定反映後、レコード保存時の添付解除に適用されます。
                    </p>
                  </div>
                )}
                {modeId === "shared_storage" ? (
                  <div className="rounded-lg border border-border bg-muted/20 p-4">
                    <div className="space-y-2">
                      <Label htmlFor="sharedStorageAccessMode">共有ストレージ権限</Label>
                      <select
                        id="sharedStorageAccessMode"
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                        value={sharedStorageAccessMode}
                        onChange={(event) =>
                          setSharedStorageAccessMode(
                            event.target.value as SharedStorageAccessMode,
                          )
                        }
                      >
                        <option value="read_only">読込のみ</option>
                        <option value="read_write">読み書き</option>
                      </select>
                    </div>
                    <p className="mt-2 text-xs text-muted-foreground">
                      読込のみでは参照、ダウンロード、既存ファイルのショートカット添付を許可します。読み書きではアップロード、フォルダ作成、名前変更、移動、実ファイル削除も許可します。
                    </p>
                  </div>
                ) : null}

                <details className="rounded-lg border border-border">
                  <summary className="cursor-pointer select-none px-4 py-3 text-sm font-medium">
                    詳細設定
                  </summary>
                  <div className="space-y-3 px-4 pb-4 pt-2">
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={headerLauncherEnabled}
                        onChange={(e) => setHeaderLauncherEnabled(e.target.checked)}
                      />
                      ヘッダ起動を有効化
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={effectiveReplaceNativeAttachmentFields}
                        disabled={modeId === "shared_storage"}
                        onChange={(e) => setReplaceNativeAttachmentFields(e.target.checked)}
                      />
                      標準添付差し替えを有効化
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={showHiddenStorageEntries}
                        onChange={(e) => setShowHiddenStorageEntries(e.target.checked)}
                      />
                      隠しファイルを表示
                    </label>

                    <div className="space-y-2 border-t border-border pt-4">
                      <Label htmlFor="maxUploadFileBytes">1ファイルのアップロード上限（MiB・任意）</Label>
                      <Input id="maxUploadFileBytes" inputMode="numeric" value={maxUploadFileBytes}
                        onChange={(event) => setMaxUploadFileBytes(event.target.value)} />
                      <p className="text-xs text-muted-foreground">
                        未設定時はストレージ設定を使用します。指定する場合は1〜10,240MiBかつストレージ上限以下です。保存後の新規アップロードに適用します。
                        既存ファイルのダウンロードや、同じ保存先を使う他アプリには影響しません。
                      </p>
                      {selectedStorage && <p className="text-xs text-muted-foreground">ストレージ上限: {selectedStorage.maxUploadFileBytes / 1024 ** 2}MiB</p>}
                    </div>

                    <div className="border-t border-border pt-4">
                      <div className="mb-3">
                        <div className="font-medium">サムネイル生成</div>
                        <p className="mt-1 text-xs text-muted-foreground">
                          このアプリ設定で互換サムネイルを生成する元画像の上限です。公式上限を超える値は指定できません。
                        </p>
                      </div>
                      <div className="grid gap-3 md:grid-cols-2">
                        <div className="space-y-2">
                          <Label htmlFor="thumbnailSourceMaxBytes">
                            サムネイル化対象最大サイズ（bytes）
                          </Label>
                          <Input
                            id="thumbnailSourceMaxBytes"
                            inputMode="numeric"
                            aria-invalid={thumbnailSourceMaxBytesInvalid}
                            value={thumbnailSourceMaxBytes}
                            onChange={(e) => setThumbnailSourceMaxBytes(e.target.value)}
                          />
                          <p className="text-xs text-muted-foreground">
                            1 から {DEFAULT_THUMBNAIL_SOURCE_MAX_BYTES.toLocaleString("ja-JP")} まで
                          </p>
                          {thumbnailSourceMaxBytesInvalid ? (
                            <p className="text-xs text-destructive">
                              公式上限以下の正の整数を入力してください。
                            </p>
                          ) : null}
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="thumbnailSourceMaxPixels">
                            サムネイル化対象最大画素数
                          </Label>
                          <Input
                            id="thumbnailSourceMaxPixels"
                            inputMode="numeric"
                            aria-invalid={thumbnailSourceMaxPixelsInvalid}
                            value={thumbnailSourceMaxPixels}
                            onChange={(e) => setThumbnailSourceMaxPixels(e.target.value)}
                          />
                          <p className="text-xs text-muted-foreground">
                            1 から {DEFAULT_THUMBNAIL_SOURCE_MAX_PIXELS.toLocaleString("ja-JP")} まで
                          </p>
                          {thumbnailSourceMaxPixelsInvalid ? (
                            <p className="text-xs text-destructive">
                              公式上限以下の正の整数を入力してください。
                            </p>
                          ) : null}
                        </div>
                      </div>
                    </div>

                    {modeId === "attachment_compatible" ? (
                      <div className="space-y-2">
                        <Label>レコードフォルダの命名方法</Label>
                        <p className="text-sm">レコードIDのみ</p>
                        {recordFolderKind !== "record_id" ? (
                          <p role="alert" className="text-sm text-destructive">
                            この設定には未公開のレコードフォルダ方式が含まれるため、保存できません。
                          </p>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                </details>

                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleBackFromSharedSettings}
                  >
                    戻る
                  </Button>
                  <Button
                    type="button"
                    onClick={() => setWizardStep("fields")}
                    disabled={!storageId}
                  >
                    次へ
                  </Button>
                </div>
              </>
            ) : null}

            {wizardStep === "fields" ? (
              <>
                <FieldBindingsEditor
                  fieldBindings={fieldBindings}
                  effectiveKintoneAppId={effectiveKintoneAppId}
                  isImporting={isImporting}
                  readOnly={isKintoneDependentActionBlocked}
                  onImport={() => void handleImportFieldSettings()}
                  onSelectAll={() => updateAllFieldSelections(true)}
                  onClearSelection={() => updateAllFieldSelections(false)}
                  onUpdate={updateFieldBinding}
                />
                <div className="flex gap-3">
                  <Button type="button" variant="outline" onClick={() => setWizardStep("storage")}>
                    戻る
                  </Button>
                  <Button
                    type="button"
                    onClick={() => setWizardStep("save")}
                  >
                    次へ
                  </Button>
                </div>
              </>
            ) : null}

            {wizardStep === "save" ? (
              <>
                <div className="grid gap-4 rounded-lg border border-border bg-muted/20 p-4 text-sm md:grid-cols-2">
                  <div>
                    <div className="text-xs text-muted-foreground">対象アプリ</div>
                    <div>{effectiveKintoneAppName}</div>
                    <div className="font-mono text-xs text-muted-foreground">
                      {effectiveKintoneAppId}
                    </div>
                  </div>
	                  <div>
	                    <div className="text-xs text-muted-foreground">保存先ストレージ</div>
	                    <div>{selectedStorage?.displayName ?? "-"}</div>
	                  </div>
                  {modeId === "attachment_compatible" ? (
                    <div>
                      <div className="text-xs text-muted-foreground">レコードフォルダの命名方法</div>
                      <div>{getRecordFolderKindLabel(recordFolderKind)}</div>
                    </div>
                  ) : null}
	                  {modeId === "shared_storage" ? (
	                    <div>
	                      <div className="text-xs text-muted-foreground">共有ストレージ権限</div>
	                      <div>
	                        {t(
	                          `edit.sharedStorageAccessModes.${sharedStorageAccessModeKey(
	                            sharedStorageAccessMode,
	                          )}`,
	                        )}
	                      </div>
	                    </div>
	                  ) : null}
	                  <div>
	                    <div className="text-xs text-muted-foreground">対象フィールド数</div>
                    <div>{normalizedFieldBindings.length} 件</div>
                  </div>
                </div>
                {normalizedFieldBindings.length === 0 ? (
                  <div className="rounded-md border border-dashed border-border p-4 text-sm text-muted-foreground">
                    対象フィールドは設定されていません。すべての FILE フィールドは kintone 標準添付のまま保存されます。
                  </div>
                ) : null}
                {!isEditing ? <div className="space-y-2 rounded border p-3 text-sm">
                  <label className="flex items-start gap-2"><input type="checkbox" checked={automaticAlignmentStartOnSetup} onChange={event => setAutomaticAlignmentStartOnSetup(event.target.checked)} />設定完了後に自動整合化を開始する</label>
                  <MaintenanceHelp label="自動整合化">保存先の設定に合わせて、添付ファイルの移行や表示の修復を自動で行います。初めて使う場合はチェックを付けたままで構いません。</MaintenanceHelp>
                  <p>{automaticAlignmentStartOnSetup ? "以前の添付ファイルを復元するための再設定では、チェックを外してください。" : "自動整合化を停止した状態で設定します。復元が完了したら、設定の詳細画面で有効にできます。"}</p>
                </div> : null}
                {hydratedBinding ? <AppBindingSettingsSaveGate controller={automaticAlignment} /> : null}
                <div className="flex gap-3">
                  <Button type="button" variant="outline" onClick={() => setWizardStep("fields")}>
                    戻る
                  </Button>
                  <Button
                    onClick={() => void handleSubmit()}
                    disabled={!canSave || isSaving}
                  >
                    {isEditing ? "保存" : "作成"}
                  </Button>
                </div>
              </>
            ) : null}

            {visibleStatusMessage ? (
              <div className="rounded-md bg-primary/10 p-3 text-sm">{visibleStatusMessage}</div>
            ) : null}
            {!pluginLinkageActivated && lastReflection ? (
              <div className="rounded-md bg-muted/30 p-3">
                <div className="text-xs text-muted-foreground">プラグイン連携トークン</div>
                <div className="mt-1 break-all font-mono text-xs">
                  {lastReflection.handoffToken}
                </div>
                <div className="mt-2 text-xs text-muted-foreground">
                  期限: {new Date(lastReflection.handoffExpiresAt).toLocaleString("ja-JP")}
                </div>
              </div>
            ) : null}
            {appLimitReached ? (
              <AppLimitReachedNotice isAdmin={user?.currentMembership?.role === "customer_admin"} />
            ) : null}
            {submitError ||
            saveError ||
            importError ||
            pluginInstallationError ||
            reflectionError ||
            disconnectPluginLinkageError ||
            deleteAppBindingError ||
            bindingError ||
            storageError ? (
              <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                {submitError ||
                  saveError ||
                  importError ||
                  pluginInstallationError ||
                  reflectionError ||
                  disconnectPluginLinkageError ||
                  deleteAppBindingError ||
                  bindingError ||
                  storageError}
              </div>
            ) : null}
          </CardContent>
        </Card>
        <ConfirmDialog
          open={settingsSaveConfirmation !== null}
          onClose={() => setSettingsSaveConfirmation(null)}
          onConfirm={() => settingsSaveConfirmation ? handleSubmit(settingsSaveConfirmation) : undefined}
          title="設定を確認して保存"
          description={<div className="space-y-3">
            {settingsSaveConfirmation ? <>
              {settingsSaveConfirmation.fields.length ? <div>
                <p>外部添付フィールドは{settingsSaveConfirmation.fields.length}件です。</p>
                <p>{settingsSaveConfirmation.fields.join("、")}</p>
              </div> : <p>外部添付フィールドはありません。</p>}
              <p>自動整合化：{settingsSaveConfirmation.automaticAlignmentEnabled ? "有効" : "無効"}</p>
              {settingsSaveConfirmation.fields.length ? settingsSaveConfirmation.automaticAlignmentEnabled ? <>
                <p>標準／外部を切り替えた添付フィールドの既存ファイルは、設定反映後に新しい保存先へ自動で移行されます。</p>
                <p>外部への移行が完了したファイルは、kintone標準添付から削除されます。元の保存先に戻す場合は、設定を元に戻して保存し、kintoneに反映してください。</p>
              </> : <>
                <p>新しく追加する添付ファイルは、設定された保存先に保存されます。</p>
                <p>標準／外部を切り替えた添付フィールドの既存ファイルは、自動では移行されません。設定反映後、自動整合化を有効にするか、手動で整合化を実行してください。</p>
                <p>外部への移行が完了したファイルは、kintone標準添付から削除されます。</p>
              </> : settingsSaveConfirmation.automaticAlignmentEnabled ? <>
                <p>添付ファイルはkintone標準添付に保存されます。</p>
                <p>外部から標準に切り替えた添付フィールドの既存ファイルは、設定反映後にkintone標準添付へ自動で移行されます。</p>
              </> : <>
                <p>新しく追加する添付ファイルは、kintone標準添付に保存されます。</p>
                <p>外部から標準に切り替えた添付フィールドの既存ファイルは、自動では移行されません。設定反映後、自動整合化を有効にするか、手動で整合化を実行してください。</p>
              </>}
            </> : null}
            {settingsSaveConfirmation?.pending ? <p>継続待ちの整合化処理を中止して、設定を保存します。よろしいですか？</p> : null}
          </div>}
          confirmText="確認して保存"
          isLoading={isSaving}
        />
      </div>
    </div>
  );
}
